/**
 * Admin dashboard — operator surface.
 *
 * Three tabs aligned with FDA / HIPAA minimums:
 *   · Users  — Zitadel-backed roster + CRUD (Phase B.1).
 *   · Audit  — redirect into the dedicated AuditorPage (hash-chain
 *              verified log). Rendered as a summary card + link so
 *              the full page stays the canonical review surface.
 *   · System — software version, build hash, SBOM link, pod health,
 *              AI model inventory (Phase B.5 will populate).
 *
 * Regulatory mapping:
 *   · 21 CFR Part 11 §11.10(d) — system validation: version +
 *     build + SBOM visible to admin for traceability.
 *   · HIPAA §164.308(a)(4)    — workforce access management:
 *     Users tab fulfils the CRUD + role-assignment requirement.
 *   · FDA cybersecurity (2023) — SBOM access + patch status.
 */
import { createEffect, createSignal, For, onMount, Show } from "solid-js";
import { createQuery } from "@tanstack/solid-query";
import { A } from "@solidjs/router";

import { auth } from "../auth/store";
import { canManageLicenses } from "../auth/types";
type Tab = "users" | "cases" | "audit" | "access" | "sessions" | "retention" | "recycle" | "system" | "licenses";

interface AdminUser {
  id: string; userName: string; firstName: string; lastName: string;
  email: string; state: string; roles: string[];
  lastLogin: string | null; createdAt: string | null; changedAt: string | null;
}

interface HealthPod {
  name: string; phase: string; ready: number; total: number;
}
interface HealthView { pods: HealthPod[]; storage?: any; tls?: any; build?: any; }

async function fetchUsers(): Promise<AdminUser[]> {
  const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
  const rsp = await fetch("/viewer-api/admin/users", {
    headers: { Authorization: `Bearer ${auth?.accessToken ?? ""}` },
  });
  if (rsp.status === 503) throw new Error("identity-provider PAT not configured on server");
  if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
  return rsp.json();
}
async function fetchHealth(): Promise<HealthView> {
  const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
  const rsp = await fetch("/viewer-api/admin/health", {
    headers: { Authorization: `Bearer ${auth?.accessToken ?? ""}` },
  });
  if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
  return rsp.json();
}
async function fetchRoles(): Promise<{ key: string; displayName: string }[]> {
  const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
  const rsp = await fetch("/viewer-api/admin/roles", {
    headers: { Authorization: `Bearer ${auth?.accessToken ?? ""}` },
  });
  if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
  return rsp.json();
}
async function fetchAuditSummary(): Promise<{ total: number; broken: number[]; missing_prev: number[] }> {
  const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
  const rsp = await fetch("/viewer-api/audit/_verify", {
    headers: { Authorization: `Bearer ${auth?.accessToken ?? ""}` },
  });
  if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
  return rsp.json();
}

export function AdminDashboard() {
  const [tab, setTab] = createSignal<Tab>("users");
  return (
    <div class="flex h-full flex-col">
      <header class="flex items-center gap-3 border-b border-ink-300 bg-ink-100 px-4 py-3">
        <span class="chip-brand">Admin</span>
        <span class="text-xs text-ink-700">
          Workforce &amp; system administration · 21 CFR Part 11 · HIPAA §164.308
        </span>
      </header>
      <div class="flex border-b border-ink-300">
        <TabBtn active={tab() === "users"}    onClick={() => setTab("users")}>Users</TabBtn>
        <TabBtn active={tab() === "cases"}    onClick={() => setTab("cases")}>Cases</TabBtn>
        <TabBtn active={tab() === "audit"}    onClick={() => setTab("audit")}>Audit</TabBtn>
        <TabBtn active={tab() === "access"}   onClick={() => setTab("access")}>Access report</TabBtn>
        <TabBtn active={tab() === "sessions"}  onClick={() => setTab("sessions")}>Sessions</TabBtn>
        <TabBtn active={tab() === "retention"} onClick={() => setTab("retention")}>Retention</TabBtn>
        <TabBtn active={tab() === "recycle"}   onClick={() => setTab("recycle")}>Recycle bin</TabBtn>
        <Show when={canManageLicenses(auth())}><TabBtn active={tab() === "licenses"}  onClick={() => setTab("licenses")}>Desktop licenses</TabBtn></Show>
        <TabBtn active={tab() === "system"}    onClick={() => setTab("system")}>System</TabBtn>
      </div>
      <div class="flex-1 min-h-0 overflow-auto">
        <Show when={tab() === "users"}>    <UsersTab /> </Show>
        <Show when={tab() === "cases"}>    <CasesTab /> </Show>
        <Show when={tab() === "audit"}>    <AuditTab /> </Show>
        <Show when={tab() === "access"}>   <AccessReportTab /> </Show>
        <Show when={tab() === "sessions"}>  <SessionsTab /> </Show>
        <Show when={tab() === "retention"}> <RetentionTab /> </Show>
        <Show when={tab() === "recycle"}>   <RecycleBinTab /> </Show>
        <Show when={tab() === "licenses" && canManageLicenses(auth())}>  <DesktopLicensesTab /></Show>
        <Show when={tab() === "system"}>    <SystemTab /></Show>
      </div>
    </div>
  );
}

function TabBtn(p: { active: boolean; onClick: () => void; children: any }) {
  return (
    <button type="button"
            class="px-4 py-2 text-[11px] font-semibold uppercase tracking-wider"
            classList={{
              "text-white border-b-2 border-brand": p.active,
              "text-ink-700 hover:text-white":     !p.active,
            }}
            onClick={p.onClick}>
      {p.children}
    </button>
  );
}

// ------------------------------------------------------------
//  Users
// ------------------------------------------------------------
function UsersTab() {
  const users = createQuery(() => ({
    queryKey: ["admin-users"],
    queryFn:  fetchUsers,
    refetchInterval: 60_000,
  }));
  const roles = createQuery(() => ({
    queryKey: ["admin-roles"],
    queryFn:  fetchRoles,
  }));
  const [creating, setCreating] = createSignal(false);
  const [form, setForm] = createSignal({ userName: "", firstName: "", lastName: "", email: "", password: "" });
  const [busy, setBusy] = createSignal<string | null>(null);
  const [err,  setErr]  = createSignal<string | null>(null);
  const [openRoles, setOpenRoles] = createSignal<string | null>(null);
  const [grants, setGrants] = createSignal<Record<string, { grantId: string; roleKeys: string[] }[]>>({});
  // Password-reset modal state — displayed after successful /password/reset
  // so the admin can copy the one-time temp. Cleared on close; not persisted.
  const [resetShow, setResetShow] = createSignal<null | { userName: string; password?: string; mode: string }>(null);
  // MFA enrollment modal state — holds the (single-use) secret +
  // QR-encodable otpauth URL returned by /mfa/enroll. Never
  // persisted; only shown in-memory until dismissed.
  const [mfaShow, setMfaShow] = createSignal<null | { userName: string; userId: string; secret: string; url: string; email: string }>(null);

  // Standard RBAC: owner accounts are managed only by other owners.
  // A non-owner admin can list/search owner rows (read), but cannot
  // suspend / unlock / reset / delete / enrol MFA / edit grants
  // on them (write).  The backend enforces this with
  // denyIfOwnerTarget(); we mirror it client-side so the buttons
  // simply do not appear for users who couldn't use them anyway.
  const viewerIsOwner   = () => (auth()?.roles ?? []).includes("owner");
  const userIsOwner     = (u: AdminUser) => u.roles.includes("owner");
  // The admin Users tab is for managing OTHER users.  Hide every
  // mutation control on the actor's own row — self-management
  // (rotate password, enrol MFA) belongs in a personal-profile
  // page; recovery (forgot password) belongs in the
  // identity-provider's "Reset password" link on the sign-in
  // page.  The server enforces this with denyIfSelfTarget().
  const userIsSelf      = (u: AdminUser) => u.id === auth()?.claims?.sub;
  const canManageRow    = (u: AdminUser) =>
    !userIsSelf(u) && (viewerIsOwner() || !userIsOwner(u));

  async function loadGrants(userId: string) {
    const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
    const rsp = await fetch(`/viewer-api/admin/users/${userId}/grants`, {
      headers: { Authorization: `Bearer ${auth?.accessToken ?? ""}` },
    });
    if (!rsp.ok) return;
    const data = await rsp.json();
    setGrants((g) => ({ ...g, [userId]: data }));
  }

  async function toggleRole(userId: string, roleKey: string, on: boolean) {
    const key = `${userId}:${roleKey}`;
    setBusy(key);
    setErr(null);
    try {
      const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
      if (on) {
        const rsp = await fetch(`/viewer-api/admin/users/${userId}/grants`, {
          method: "POST",
          headers: { "content-type": "application/json",
                     Authorization: `Bearer ${auth?.accessToken ?? ""}` },
          body: JSON.stringify({ roleKey }),
        });
        if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
      } else {
        const g = (grants()[userId] ?? []).find((x) => x.roleKeys.includes(roleKey));
        if (g) {
          const rsp = await fetch(`/viewer-api/admin/users/${userId}/grants/${g.grantId}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${auth?.accessToken ?? ""}` },
          });
          if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
        }
      }
      await loadGrants(userId);
      await users.refetch();
    } catch (e) {
      setErr(`${on ? "grant" : "revoke"} failed: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  }

  async function doMutate(userId: string, action: "deactivate" | "reactivate" | "unlock" | "delete") {
    setBusy(userId + "/" + action);
    setErr(null);
    try {
      const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
      const path = action === "delete"
        ? `/viewer-api/admin/users/${userId}`
        : `/viewer-api/admin/users/${userId}/${action}`;
      const rsp = await fetch(path, {
        method: action === "delete" ? "DELETE" : "POST",
        headers: { Authorization: `Bearer ${auth?.accessToken ?? ""}` },
      });
      if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
      await users.refetch();
    } catch (e) {
      setErr(`${action} failed: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  }

  async function doCreate(e: Event) {
    e.preventDefault();
    setBusy("create");
    setErr(null);
    try {
      const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
      const payload: any = { ...form() };
      // Empty password → server auto-generates a policy-compliant
      // temp password and returns it in the response.  This avoids
      // the INITIAL-state stranding that happens when neither the
      // admin nor the email provider supplies a first password.
      if (!payload.password) delete payload.password;
      const rsp = await fetch("/viewer-api/admin/users", {
        method: "POST",
        headers: { "content-type": "application/json",
                   Authorization: `Bearer ${auth?.accessToken ?? ""}` },
        body: JSON.stringify(payload),
      });
      if (!rsp.ok) throw new Error(`HTTP ${rsp.status} — ${await rsp.text()}`);
      const j = await rsp.json() as any;
      const created = { ...form() };
      setCreating(false);
      setForm({ userName: "", firstName: "", lastName: "", email: "", password: "" });
      await users.refetch();
      // If the server auto-generated a temp password, surface it once
      // to the admin via the same modal the temp-reset path uses.
      if (j.auto_generated && j.password) {
        setResetShow({ userName: created.userName, password: j.password, mode: "temp" });
      }
    } catch (e) { setErr((e as Error).message); }
    finally    { setBusy(null); }
  }

  // Password reset — modes: "temp" (server generates a policy-
  // compliant password and returns it once) or "email" (Zitadel
  // sends its own reset link). Temp mode pops a modal showing the
  // password so the admin can copy it; the password is never
  // stored in localStorage or anywhere client-side.
  async function resetPassword(u: AdminUser, mode: "temp" | "email") {
    const key = `${u.id}/reset/${mode}`;
    setBusy(key); setErr(null);
    try {
      const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
      const rsp = await fetch(`/viewer-api/admin/users/${u.id}/password/reset`, {
        method: "POST",
        headers: { "content-type": "application/json",
                   Authorization: `Bearer ${auth?.accessToken ?? ""}` },
        body: JSON.stringify({ mode }),
      });
      if (!rsp.ok) {
        const txt = await rsp.text();
        // Friendly error for INITIAL-state users — Zitadel won't let
        // us bypass the init-email flow from the admin side.
        if (/user_is_initial|not yet initialized|COMMAND-M9dse/.test(txt)) {
          throw new Error(
            `${u.userName} is in INITIAL state (account never completed first-login). ` +
            `Delete and recreate the account with an initial password set, or ask the user ` +
            `to click the link in the activation email they received on account creation.`
          );
        }
        throw new Error(`${rsp.status} — ${txt}`);
      }
      const j = await rsp.json() as any;
      setResetShow({ userName: u.userName, password: j.password, mode });
    } catch (e) {
      setErr(`password reset failed: ${(e as Error).message}`);
    } finally { setBusy(null); }
  }

  async function enrollMfa(u: AdminUser, force = false) {
    const key = `${u.id}/mfa`;
    setBusy(key); setErr(null);
    try {
      const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
      const rsp = await fetch(`/viewer-api/admin/users/${u.id}/mfa/enroll${force ? "?force=1" : ""}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${auth?.accessToken ?? ""}` },
      });
      if (!rsp.ok) {
        const body = await rsp.text();
        if (/already set up/i.test(body)) {
          if (confirm(`${u.userName} already has MFA enrolled. Remove existing factor and re-enroll?`)) {
            return enrollMfa(u, true);
          }
        }
        throw new Error(`${rsp.status} — ${body}`);
      }
      const j = await rsp.json() as any;
      setMfaShow({ userName: u.userName, userId: u.id, secret: j.secret, url: j.url, email: u.email });
    } catch (e) {
      setErr(`MFA enroll failed: ${(e as Error).message}`);
    } finally { setBusy(null); }
  }

  return (
    <div class="p-4">
      <div class="mb-3 flex items-center gap-3">
        <h3 class="text-sm font-semibold text-white">Users</h3>
        <span class="text-[11px] text-ink-700">
          {users.data ? `${users.data.length} user(s) — source: identity provider` : "loading…"}
        </span>
        <button class="btn-ghost ml-auto text-xs"
                onClick={() => setCreating((v) => !v)}>
          {creating() ? "Cancel" : "+ Invite user"}
        </button>
      </div>

      <Show when={err()}>
        <div class="mb-2 rounded border border-danger bg-danger/10 p-2 text-[11px] text-danger">{err()}</div>
      </Show>

      <Show when={creating()}>
        <form class="mb-3 grid grid-cols-2 gap-2 rounded border border-ink-300 bg-ink-100 p-3 text-xs"
              onSubmit={doCreate}>
          <Field label="Username" value={form().userName}  onInput={(v) => setForm({ ...form(), userName: v })} />
          <Field label="Email"    value={form().email}     onInput={(v) => setForm({ ...form(), email: v })} />
          <Field label="First"    value={form().firstName} onInput={(v) => setForm({ ...form(), firstName: v })} />
          <Field label="Last"     value={form().lastName}  onInput={(v) => setForm({ ...form(), lastName: v })} />
          <PasswordField label="Initial password (optional)"
                         value={form().password}
                         placeholder="blank = send setup email via identity provider"
                         onInput={(v) => setForm({ ...form(), password: v })} />
          <div class="col-span-2 flex items-center justify-between gap-2">
            <span class="text-[10px] text-ink-700 leading-relaxed">
              If set, user is created with this password AND forced to change it on first
              login (NIST 800-63B + HIPAA §164.308(a)(5)). Leave blank to have the identity provider send a
              setup email instead. Password is never logged.
            </span>
            <button class="btn-primary text-xs" type="submit"
                    disabled={busy() === "create"}>
              {busy() === "create" ? "Creating…" : "Create user"}
            </button>
          </div>
        </form>
      </Show>

      <Show when={users.error}>
        <div class="rounded border border-warn bg-warn/10 p-3 text-[11px] text-warn">
          {(users.error as Error).message}
        </div>
      </Show>
      <Show when={users.data}>
        <div class="overflow-x-auto rounded border border-ink-300">
          <table class="min-w-full text-[11px]">
            <thead class="bg-ink-100 text-[10px] uppercase tracking-wider text-ink-700">
              <tr>
                <Th>Username</Th><Th>Name</Th><Th>Email</Th><Th>State</Th>
                <Th>Roles</Th><Th>Last login</Th><Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              <For each={users.data}>{(u) => <>
                <tr class="border-t border-ink-300 hover:bg-ink-200/30">
                  <Td class="font-mono text-white">{u.userName}</Td>
                  <Td>{[u.firstName, u.lastName].filter(Boolean).join(" ") || "—"}</Td>
                  <Td class="text-ink-700">{u.email || "—"}</Td>
                  <Td><StateChip state={u.state} /></Td>
                  <Td>
                    <div class="flex items-center gap-1">
                      <Show when={u.roles.length > 0} fallback={<span class="text-ink-700">—</span>}>
                        <For each={u.roles}>{(r) => <span class="chip-brand !text-[9px]">{r}</span>}</For>
                      </Show>
                      <Show when={canManageRow(u)}>
                        <button type="button"
                                class="ml-1 rounded border border-ink-300 px-1 text-[10px] text-ink-700 hover:border-brand hover:text-white"
                                title="Edit role grants"
                                onClick={() => {
                                  if (openRoles() === u.id) { setOpenRoles(null); return; }
                                  setOpenRoles(u.id);
                                  loadGrants(u.id);
                                }}>
                          {openRoles() === u.id ? "×" : "✎"}
                        </button>
                      </Show>
                    </div>
                  </Td>
                  <Td class="text-ink-700">{u.lastLogin ? fmtRelative(u.lastLogin) : "—"}</Td>
                  <Td>
                    <Show when={canManageRow(u)} fallback={
                      <span class="text-[10px] uppercase tracking-wider text-ink-700"
                            title={
                              userIsSelf(u)
                                ? "Use the user-profile page or the sign-in page's Reset password link for your own account."
                                : "Owner accounts can only be managed by another owner."
                            }>
                        {userIsSelf(u) ? "you — signed in" : "owner — view-only"}
                      </span>
                    }>
                      <div class="flex gap-1">
                        <Show when={u.state === "active"}>
                          <ActionBtn label="Suspend"
                                     onClick={() => doMutate(u.id, "deactivate")}
                                     busy={busy() === u.id + "/deactivate"} />
                        </Show>
                        <Show when={u.state === "inactive"}>
                          <ActionBtn label="Reactivate"
                                     onClick={() => doMutate(u.id, "reactivate")}
                                     busy={busy() === u.id + "/reactivate"} />
                        </Show>
                        <Show when={u.state === "locked"}>
                          <ActionBtn label="Unlock"
                                     onClick={() => doMutate(u.id, "unlock")}
                                     busy={busy() === u.id + "/unlock"} />
                        </Show>
                        <ActionBtn label="Reset pwd"
                                   onClick={() => {
                                     // Owner-target reset is email-only — the
                                     // server rejects mode=temp for owner rows
                                     // (no temp materialised in another admin's
                                     // UI).  Skip the mode prompt and call
                                     // straight to email so the click feels
                                     // identical to other rows.
                                     if (userIsOwner(u)) { resetPassword(u, "email"); return; }
                                     const m = prompt(`Reset password for ${u.userName}:\n\n  temp  — generate a one-time password I can deliver out-of-band\n  email — send the identity-provider reset email\n\nType temp or email:`, "temp");
                                     if (m === "temp" || m === "email") resetPassword(u, m);
                                   }}
                                   busy={busy() === u.id + "/reset/temp" || busy() === u.id + "/reset/email"} />
                        <ActionBtn label="Enroll MFA"
                                   onClick={() => enrollMfa(u)}
                                   busy={busy() === u.id + "/mfa"} />
                        <ActionBtn label="Delete" danger
                                   onClick={() => {
                                     if (confirm(`Delete ${u.userName}? This cannot be undone. Audit-logged.`))
                                       doMutate(u.id, "delete");
                                   }}
                                   busy={busy() === u.id + "/delete"} />
                      </div>
                    </Show>
                  </Td>
                </tr>
                <Show when={openRoles() === u.id}>
                  <tr class="border-t border-ink-300 bg-ink-200/40">
                    <td colspan="7" class="p-3">
                      <div class="text-[10px] uppercase tracking-wider text-ink-700">
                        Role grants · project vaultpod
                      </div>
                      <Show when={roles.data} fallback={<div class="text-[11px] text-ink-700">loading roles…</div>}>
                        <div class="mt-1 flex flex-wrap gap-2">
                          <For each={(roles.data ?? []).filter((r) => r.key !== "owner" || viewerIsOwner())}>{(r) => {
                            const has = () => u.roles.includes(r.key);
                            return (
                              <button type="button"
                                      class="inline-flex items-center gap-1 rounded border px-2 py-1 text-[10px]"
                                      classList={{
                                        "border-brand bg-brand/20 text-white": has(),
                                        "border-ink-300 text-ink-700 hover:border-brand hover:text-white": !has(),
                                      }}
                                      disabled={busy() === `${u.id}:${r.key}`}
                                      onClick={() => toggleRole(u.id, r.key, !has())}>
                                {has() ? "✓ " : "+ "}
                                <span class="font-mono">{r.key}</span>
                                <Show when={r.displayName !== r.key}>
                                  <span class="text-ink-700">· {r.displayName}</span>
                                </Show>
                              </button>
                            );
                          }}</For>
                        </div>
                      </Show>
                      <div class="mt-2 text-[10px] text-ink-700">
                        Click a role to grant / revoke. Every toggle is
                        audit-logged (user.role_assign / user.role_revoke)
                        with actor + target + role + timestamp.
                      </div>
                    </td>
                  </tr>
                </Show>
              </>}</For>
            </tbody>
          </table>
        </div>
      </Show>

      {/* Password-reset result modal — shows the temp password ONCE
          so the admin can copy / print / hand over. Never stored. */}
      <Show when={resetShow()}>
        <Modal onClose={() => setResetShow(null)}>
          <h2 class="text-base font-semibold text-white">Password reset</h2>
          <div class="mt-2 text-[11px] text-ink-700">
            <span class="chip-brand !text-[9px]">{resetShow()!.userName}</span>
            <span class="ml-2">mode: {resetShow()!.mode}</span>
          </div>
          <Show when={resetShow()!.mode === "temp" && resetShow()!.password}>
            <div class="mt-3 rounded border border-warn bg-warn/10 p-3">
              <div class="text-[10px] uppercase tracking-wider text-warn">one-time password</div>
              <div class="mt-1 font-mono text-lg text-white select-all">
                {resetShow()!.password}
              </div>
              <div class="mt-2 text-[10px] text-ink-700 leading-relaxed">
                Deliver this to the user out-of-band. It is shown <b>once</b>. User will
                be forced to choose a new password on first login. The password is never
                stored on this page and is not in the audit record (only its length is
                logged).
              </div>
              <button class="btn-ghost mt-2 text-xs"
                      onClick={() => navigator.clipboard.writeText(resetShow()!.password!).catch(() => {})}>
                Copy to clipboard
              </button>
            </div>
          </Show>
          <Show when={resetShow()!.mode === "email"}>
            <div class="mt-3 text-[11px] text-ink-700">
              A password-reset email has been sent to the user. The reader clicks
              the link, chooses a new password, and is asked for MFA on next login.
            </div>
          </Show>
        </Modal>
      </Show>

      {/* MFA enrollment modal — QR + plain secret + PDF + mailto
          delivery. Never stored; closing the modal drops it. */}
      <Show when={mfaShow()}>
        <MfaModal info={mfaShow()!} onClose={() => setMfaShow(null)} />
      </Show>
    </div>
  );
}

/** QR modal — renders the otpauth URL as a 256×256 PNG (canvas),
 *  exposes download + email. */
function MfaModal(p: { info: { userName: string; userId: string; secret: string; url: string; email: string }; onClose: () => void }) {
  let canvas!: HTMLCanvasElement;
  const [pngDataUrl, setPngDataUrl] = createSignal<string>("");
  const [verifyCode, setVerifyCode] = createSignal("");
  const [verifyErr, setVerifyErr]   = createSignal<string | null>(null);
  const [verifyOk,  setVerifyOk]    = createSignal(false);

  // Render the QR on mount. Uses the `qrcode` package so we stay
  // client-side — the secret never leaves the admin's browser
  // beyond the original Zitadel fetch.
  onMount(async () => {
    const qr = await import("qrcode");
    await qr.toCanvas(canvas, p.info.url, { width: 256, margin: 1,
                                            errorCorrectionLevel: "M" });
    setPngDataUrl(canvas.toDataURL("image/png"));
  });

  async function verify() {
    setVerifyErr(null);
    try {
      const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
      const rsp = await fetch(`/viewer-api/admin/users/${p.info.userId}/mfa/verify`, {
        method: "POST",
        headers: { "content-type": "application/json",
                   Authorization: `Bearer ${auth?.accessToken ?? ""}` },
        body: JSON.stringify({ code: verifyCode().trim() }),
      });
      if (!rsp.ok) throw new Error(await rsp.text());
      setVerifyOk(true);
    } catch (e) {
      setVerifyErr((e as Error).message);
    }
  }

  function downloadPdf() {
    // Open a print window that renders the QR + activation instructions
    // + plain secret as a fallback. Admin → Save as PDF from the
    // browser's print dialog. Simpler + deterministic than shipping
    // a pdf library.
    const secretGrouped = p.info.secret.match(/.{1,4}/g)?.join(" ") ?? p.info.secret;
    const html = `<!doctype html><html><head>
      <meta charset="utf-8" />
      <title>MFA enrollment — ${escapeHtml(p.info.userName)}</title>
      <style>
        @page { margin: 18mm 20mm; }
        body { font-family: Georgia, serif; color: #111; }
        .hdr { border-bottom: 2px solid #27a567; padding-bottom: 6pt; margin-bottom: 14pt; }
        h1   { font-size: 16pt; margin: 0 0 4pt; }
        .meta { font-size: 10pt; color: #555; }
        .qrbox { display: flex; gap: 18pt; align-items: flex-start; margin-top: 18pt; }
        .qrbox img { border: 1px solid #ccc; padding: 4pt; background: #fff; }
        .secret { font-family: ui-monospace, Menlo, monospace; font-size: 11pt;
                  letter-spacing: 0.5pt; background: #f5f5f5; padding: 6pt; border-radius: 4pt; }
        ol { font-size: 10.5pt; line-height: 1.5; }
        .warn { background: #fff3cd; border-left: 3px solid #d1a000; padding: 6pt 10pt;
                font-size: 9.5pt; margin-top: 14pt; }
      </style>
    </head><body>
      <div class="hdr">
        <h1>MFA enrollment — ${escapeHtml(p.info.userName)}</h1>
        <div class="meta">
          Issued ${new Date().toISOString().replace("T"," ").replace(/\..*/, "Z")} · Second factor: TOTP (RFC 6238).
        </div>
      </div>
      <p>Your account requires a second authentication factor. Follow the steps
         below to set up a time-based one-time password (TOTP) in your
         authenticator app.</p>
      <div class="qrbox">
        <img src="${pngDataUrl()}" width="256" height="256" alt="QR" />
        <div>
          <ol>
            <li>Install <b>Google Authenticator</b>, <b>Microsoft Authenticator</b>,
                or <b>Authy</b> on your phone.</li>
            <li>In the app, choose <i>Add account → Scan a QR code</i>.</li>
            <li>Scan the QR shown here.</li>
            <li>If scanning doesn't work, type the secret below manually.</li>
            <li>Enter the 6-digit code your app shows to your administrator to
                complete activation.</li>
          </ol>
          <div class="meta">Shared secret (manual entry):</div>
          <div class="secret">${escapeHtml(secretGrouped)}</div>
        </div>
      </div>
      <div class="warn">
        Treat this page as a credential. Shred after activation. If it is lost or
        exposed, ask your administrator to rotate MFA.
      </div>
      <script>window.onload = () => setTimeout(() => window.print(), 300);</script>
    </body></html>`;
    const w = window.open("", "_blank", "width=920,height=1100");
    if (!w) { alert("Pop-up blocked — allow pop-ups for this site."); return; }
    w.document.open(); w.document.write(html); w.document.close();
  }

  function mailto() {
    // Open the admin's default mail client with a pre-composed
    // message. The QR PNG is not attached inline (mailto: can't
    // carry attachments); the user gets the otpauth:// URL + the
    // manual-entry secret which together cover both import paths.
    //
    // For hospitals with an SMTP relay, a follow-up can add a real
    // server-side email flow — tracked in the evidence doc.
    const subject = encodeURIComponent(`MFA setup for ${p.info.userName}`);
    const body = encodeURIComponent(
      `Your account has a new second-factor enrollment.\n\n` +
      `1. Install Google Authenticator / Microsoft Authenticator / Authy.\n` +
      `2. In the app, Add account → Enter a setup key.\n` +
      `3. Account name: ${p.info.userName}\n` +
      `4. Key: ${p.info.secret}\n` +
      `5. Type: Time based\n\n` +
      `Alternatively, scan the QR code provided by your administrator.\n\n` +
      `Treat this message as a credential. Delete after setup.`
    );
    window.location.href = `mailto:${encodeURIComponent(p.info.email)}?subject=${subject}&body=${body}`;
  }

  const secretPretty = () => p.info.secret.match(/.{1,4}/g)?.join(" ") ?? p.info.secret;

  return (
    <Modal onClose={p.onClose}>
      <h2 class="text-base font-semibold text-white">Enroll MFA — {p.info.userName}</h2>
      <div class="mt-2 flex gap-4">
        <canvas ref={canvas} class="rounded bg-white p-1" style={{ width: "256px", height: "256px" }} />
        <div class="flex-1">
          <div class="text-[10px] uppercase tracking-wider text-ink-700">Shared secret</div>
          <div class="mt-1 select-all break-all font-mono text-[11px] text-white">
            {secretPretty()}
          </div>
          <div class="mt-3 text-[10px] leading-relaxed text-ink-700">
            The user imports this in <b>Google Authenticator</b>, <b>Microsoft
            Authenticator</b>, or <b>Authy</b> and starts generating 6-digit codes.
            The factor stays in a PENDING state until a valid code is verified —
            the user can do this on their next login, or you can verify here
            below on their behalf.
          </div>
          <div class="mt-3 flex items-center gap-2">
            <button class="btn-ghost text-xs" onClick={downloadPdf}>Download PDF</button>
            <button class="btn-ghost text-xs" onClick={mailto}>Email to user</button>
            <button class="btn-ghost text-xs"
                    onClick={() => navigator.clipboard.writeText(p.info.secret).catch(() => {})}>
              Copy secret
            </button>
          </div>
          <div class="mt-3 border-t border-ink-300 pt-2">
            <div class="text-[10px] uppercase tracking-wider text-ink-700">Activate now (optional)</div>
            <div class="mt-1 flex items-center gap-2">
              <input class="w-24 rounded border border-ink-300 bg-ink-200 px-2 py-1 font-mono text-[11px]"
                     placeholder="123456"
                     value={verifyCode()}
                     onInput={(e) => setVerifyCode(e.currentTarget.value)} />
              <button class="btn-primary text-xs" disabled={verifyCode().length < 6 || verifyOk()}
                      onClick={verify}>
                {verifyOk() ? "Activated ✓" : "Verify code"}
              </button>
            </div>
            <Show when={verifyErr()}>
              <div class="mt-1 text-[10px] text-danger">{verifyErr()}</div>
            </Show>
          </div>
        </div>
      </div>
      <div class="mt-3 rounded border border-warn bg-warn/10 p-2 text-[10px] leading-relaxed text-ink-700">
        This page displays credential material. Close it as soon as the
        user has captured the QR or secret — it is not recoverable.
      </div>
    </Modal>
  );
}

function Modal(p: { children: any; onClose: () => void }) {
  return (
    <div class="fixed inset-0 z-[100] grid place-items-center bg-black/70 backdrop-blur-sm"
         onClick={(e) => { if (e.target === e.currentTarget) p.onClose(); }}>
      <div class="w-[min(720px,94vw)] max-h-[90vh] overflow-auto rounded-lg border border-ink-300 bg-ink-100 p-5 shadow-2xl">
        <div class="flex justify-end">
          <button class="text-[14px] text-ink-700 hover:text-white" onClick={p.onClose}>×</button>
        </div>
        {p.children}
      </div>
    </div>
  );
}

function escapeHtml(s: string) {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" } as any)[c]);
}

function PasswordField(p: { label: string; value: string; placeholder?: string; onInput: (v: string) => void }) {
  const [show, setShow] = createSignal(false);
  // Client-side heuristic preview of the server-side policy. The
  // server is still the authority — this is just UX help so the
  // admin sees a red/green hint before submit.
  const hints = () => {
    const pw = p.value;
    const out: { ok: boolean; text: string }[] = [];
    out.push({ ok: pw.length >= 12, text: "≥ 12 chars" });
    out.push({ ok: /[A-Z]/.test(pw), text: "uppercase" });
    out.push({ ok: /[a-z]/.test(pw), text: "lowercase" });
    out.push({ ok: /\d/.test(pw),    text: "digit" });
    return out;
  };
  return (
    <label class="flex flex-col gap-0.5">
      <span class="text-[10px] uppercase tracking-wider text-ink-700">{p.label}</span>
      <div class="flex gap-1">
        <input class="flex-1 rounded border border-ink-300 bg-ink-200 px-2 py-1 font-mono text-[11px]"
               type={show() ? "text" : "password"}
               autocomplete="new-password"
               placeholder={p.placeholder}
               value={p.value}
               onInput={(e) => p.onInput(e.currentTarget.value)} />
        <button type="button"
                class="rounded border border-ink-300 px-2 text-[10px] text-ink-700 hover:text-white"
                onClick={() => setShow(!show())}>
          {show() ? "hide" : "show"}
        </button>
      </div>
      <Show when={p.value}>
        <div class="mt-0.5 flex flex-wrap gap-1">
          <For each={hints()}>{(h) => (
            <span class={"rounded px-1.5 text-[9px] " + (h.ok ? "bg-brand/20 text-brand" : "bg-ink-200 text-ink-700")}>
              {h.ok ? "✓" : "·"} {h.text}
            </span>
          )}</For>
        </div>
      </Show>
    </label>
  );
}

function Field(p: { label: string; value: string; onInput: (v: string) => void }) {
  return (
    <label class="flex flex-col gap-0.5">
      <span class="text-[10px] uppercase tracking-wider text-ink-700">{p.label}</span>
      <input class="rounded border border-ink-300 bg-ink-200 px-2 py-1 font-mono text-[11px]"
             value={p.value}
             onInput={(e) => p.onInput(e.currentTarget.value)} />
    </label>
  );
}
function StateChip(p: { state: string }) {
  const cls = p.state === "active"   ? "chip-ok"
            : p.state === "inactive" ? "chip-warn"
            : p.state === "locked"   ? "chip-danger"
            : p.state === "initial"  ? "chip-brand"
            :                          "chip-brand";
  return <span class={cls + " !text-[9px]"}>{p.state}</span>;
}
function ActionBtn(p: { label: string; onClick: () => void; busy?: boolean; danger?: boolean }) {
  return (
    <button type="button"
            class={(p.danger ? "border-danger text-danger hover:bg-danger hover:text-white"
                             : "border-ink-300 text-ink-700 hover:border-brand hover:text-white")
                 + " rounded border px-2 py-0.5 text-[10px] disabled:opacity-50"}
            disabled={p.busy}
            onClick={p.onClick}>
      {p.busy ? "…" : p.label}
    </button>
  );
}

// ------------------------------------------------------------
//  Audit (summary + link)
// ------------------------------------------------------------
function AuditTab() {
  const chain = createQuery(() => ({
    queryKey: ["admin-audit-chain"],
    queryFn:  fetchAuditSummary,
    refetchInterval: 60_000,
  }));
  return (
    <div class="p-4 space-y-3">
      <div class="rounded border border-ink-300 bg-ink-100 p-4">
        <div class="flex items-center gap-2 text-sm">
          <span class="chip-brand">Audit log</span>
          <span class="text-ink-700 text-[11px]">
            21 CFR Part 11 §11.10(e) · HIPAA §164.312(b) — append-only, hash-chained.
          </span>
        </div>
        <Show when={chain.data}>
          <div class="mt-3 grid grid-cols-3 gap-3">
            <Metric label="Rows in chain" value={String(chain.data!.total)} />
            <Metric label="Broken rows"
                    value={String(chain.data!.broken.length)}
                    danger={chain.data!.broken.length > 0} />
            <Metric label="Chain gaps"
                    value={String(chain.data!.missing_prev.length)}
                    danger={chain.data!.missing_prev.length > 0} />
          </div>
        </Show>
        <Show when={chain.error}>
          <div class="mt-2 text-[11px] text-danger">{(chain.error as Error).message}</div>
        </Show>
        <div class="mt-3 flex gap-2">
          <A href="/audit" class="btn-primary text-xs">Open full audit log →</A>
        </div>
      </div>
      <div class="text-[11px] leading-relaxed text-ink-700">
        The full log is on the dedicated Audit page. Filters there support
        actor (identity-provider subject), patient MRN, study UID, event kind prefix, and
        date range. CSV + evidence-bundle JSON exports are generated
        client-side from the fetched rows. Integrity is verified by
        recomputing the SHA-256 chain on the server
        (<code>/viewer-api/audit/_verify</code>) and surfacing broken rows
        in red here and on the audit page header.
      </div>
    </div>
  );
}
function Metric(p: { label: string; value: string; danger?: boolean }) {
  return (
    <div class="rounded border border-ink-300 bg-ink-200/50 p-3">
      <div class="text-[10px] uppercase tracking-wider text-ink-700">{p.label}</div>
      <div class={(p.danger ? "text-danger" : "text-white") + " mt-1 font-mono text-lg"}>{p.value}</div>
    </div>
  );
}

// ------------------------------------------------------------
//  System (health, version, SBOM, AI models)
// ------------------------------------------------------------
function SystemTab() {
  const health = createQuery(() => ({
    queryKey: ["admin-health"],
    queryFn:  fetchHealth,
    refetchInterval: 30_000,
  }));
  return (
    <div class="space-y-4 p-4">
      <section class="rounded border border-ink-300 bg-ink-100 p-3">
        <h3 class="mb-2 text-sm font-semibold text-white">Workload health</h3>
        <Show when={health.data?.pods} fallback={<div class="text-[11px] text-ink-700">loading…</div>}>
          <table class="min-w-full text-[11px]">
            <thead class="text-[10px] uppercase text-ink-700">
              <tr><Th>Pod</Th><Th>Phase</Th><Th>Ready</Th></tr>
            </thead>
            <tbody>
              <For each={health.data!.pods}>{(pod) => (
                <tr class="border-t border-ink-300">
                  <Td class="font-mono">{pod.name}</Td>
                  <Td>{pod.phase}</Td>
                  <Td class="font-mono">{pod.ready}/{pod.total}</Td>
                </tr>
              )}</For>
            </tbody>
          </table>
        </Show>
      </section>
      <AiModelsSection />
      <DicomPeersSection />
      <RetentionSection />
      <section class="rounded border border-ink-300 bg-ink-100 p-3">
        <h3 class="mb-2 text-sm font-semibold text-white">Build &amp; traceability</h3>
        <div class="text-[11px] text-ink-700 leading-relaxed">
          Every mutation in the <b>Users</b>, <b>AI models</b>, <b>DICOM peers</b>, and
          <b>Retention</b> sections emits an audit event with a <i>before</i> /
          <i>after</i> snapshot (per 21 CFR Part 11 §11.10(e) "old and new values"
          requirement). Chain integrity is verified on the Audit tab.
          SBOM + container digest for this release are captured in the
          deployment manifest — surfaced here in a follow-up.
        </div>
      </section>
    </div>
  );
}

/** Simple generic config-section editor — list rows, click to edit
 *  inline, delete with confirm. Used by DICOM peers, retention, AI
 *  models. Columns are declared via the `columns` prop so we can
 *  drop in new config surfaces later without another copy. */
function ConfigSection<T extends { id?: number }>(p: {
  title:   string;
  hint:    string;
  path:    string;                        // /viewer-api/admin/{path}
  columns: { key: keyof T; label: string; width?: string; mono?: boolean }[];
  editor:  (v: T, on: (next: T) => void) => any;
  blank:   () => T;
}) {
  const rows = createQuery(() => ({
    queryKey: ["admin-config", p.path],
    queryFn:  async () => {
      const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
      const rsp = await fetch(`/viewer-api/admin/${p.path}`, {
        headers: { Authorization: `Bearer ${auth?.accessToken ?? ""}` },
      });
      if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
      return rsp.json() as Promise<T[]>;
    },
  }));
  const [editing, setEditing] = createSignal<T | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [err,  setErr]  = createSignal<string | null>(null);

  async function save() {
    const v = editing();
    if (!v) return;
    setBusy(true); setErr(null);
    try {
      const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
      const rsp = await fetch(`/viewer-api/admin/${p.path}`, {
        method: "POST",
        headers: { "content-type": "application/json",
                   Authorization: `Bearer ${auth?.accessToken ?? ""}` },
        body: JSON.stringify(v),
      });
      if (!rsp.ok) throw new Error(await rsp.text());
      setEditing(null);
      await rows.refetch();
    } catch (e) { setErr((e as Error).message); }
    finally    { setBusy(false); }
  }
  async function del(id: number) {
    if (!confirm("Delete? Audit-logged.")) return;
    const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
    const rsp = await fetch(`/viewer-api/admin/${p.path}/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${auth?.accessToken ?? ""}` },
    });
    if (rsp.ok) await rows.refetch();
  }

  return (
    <section class="rounded border border-ink-300 bg-ink-100 p-3">
      <div class="mb-2 flex items-center gap-2">
        <h3 class="text-sm font-semibold text-white">{p.title}</h3>
        <span class="text-[11px] text-ink-700">{p.hint}</span>
        <button class="btn-ghost ml-auto text-xs"
                onClick={() => setEditing(p.blank())}>+ Add</button>
      </div>
      <Show when={err()}>
        <div class="mb-2 rounded border border-danger bg-danger/10 p-2 text-[10px] text-danger">{err()}</div>
      </Show>
      <Show when={editing()}>
        <div class="mb-3 rounded border border-brand bg-brand/10 p-3">
          {p.editor(editing()!, (n) => setEditing(n))}
          <div class="mt-2 flex items-center justify-end gap-2">
            <button class="btn-ghost text-xs" onClick={() => setEditing(null)}>Cancel</button>
            <button class="btn-primary text-xs" disabled={busy()} onClick={save}>
              {busy() ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </Show>
      <Show when={rows.data && rows.data.length > 0} fallback={
        <div class="text-[11px] text-ink-700">No entries.</div>
      }>
        <div class="overflow-x-auto rounded border border-ink-300">
          <table class="min-w-full text-[11px]">
            <thead class="text-[10px] uppercase text-ink-700">
              <tr>
                <For each={p.columns}>{(c) => <Th>{c.label}</Th>}</For>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              <For each={rows.data}>{(r) => (
                <tr class="border-t border-ink-300">
                  <For each={p.columns}>{(c) => (
                    <Td class={c.mono ? "font-mono" : ""}>
                      {formatCell((r as any)[c.key])}
                    </Td>
                  )}</For>
                  <Td>
                    <div class="flex gap-1">
                      <ActionBtn label="Edit" onClick={() => setEditing({ ...(r as any) })} />
                      <ActionBtn label="Delete" danger onClick={() => del((r as any).id)} />
                    </div>
                  </Td>
                </tr>
              )}</For>
            </tbody>
          </table>
        </div>
      </Show>
    </section>
  );
}

function formatCell(v: any): any {
  if (v == null || v === "") return <span class="text-ink-700">—</span>;
  if (typeof v === "boolean") return v ? <span class="chip-ok !text-[9px]">yes</span> : <span class="text-ink-700">no</span>;
  return String(v);
}

// Helper input
function In(p: { label: string; value: any; onInput: (v: string) => void; placeholder?: string; type?: string; }) {
  return (
    <label class="flex flex-col gap-0.5">
      <span class="text-[10px] uppercase tracking-wider text-ink-700">{p.label}</span>
      <input class="rounded border border-ink-300 bg-ink-200 px-2 py-1 font-mono text-[11px]"
             type={p.type ?? "text"}
             placeholder={p.placeholder}
             value={p.value ?? ""}
             onInput={(e) => p.onInput(e.currentTarget.value)} />
    </label>
  );
}
function Sel(p: { label: string; value: any; onInput: (v: string) => void; options: string[] }) {
  return (
    <label class="flex flex-col gap-0.5">
      <span class="text-[10px] uppercase tracking-wider text-ink-700">{p.label}</span>
      <select class="rounded border border-ink-300 bg-ink-200 px-2 py-1 font-mono text-[11px]"
              value={p.value ?? ""}
              onChange={(e) => p.onInput(e.currentTarget.value)}>
        <For each={p.options}>{(o) => <option value={o}>{o}</option>}</For>
      </select>
    </label>
  );
}
function Chk(p: { label: string; value: boolean; onInput: (v: boolean) => void }) {
  return (
    <label class="flex items-center gap-1.5 text-[11px]">
      <input type="checkbox" checked={p.value} onChange={(e) => p.onInput(e.currentTarget.checked)} />
      <span>{p.label}</span>
    </label>
  );
}

function DicomPeersSection() {
  return (
    <ConfigSection
      title="DICOM peers"
      hint="Application Entity directory — StorageCommit, MPPS, MWL, Query/Retrieve."
      path="peers"
      columns={[
        { key: "aet",     label: "AET",      mono: true },
        { key: "host",    label: "Host",     mono: true },
        { key: "port",    label: "Port",     mono: true },
        { key: "tls",     label: "TLS" },
        { key: "role",    label: "Role" },
        { key: "enabled", label: "Enabled" },
        { key: "note",    label: "Note" },
      ]}
      blank={() => ({ aet: "", host: "", port: 104, tls: false, role: "StorageCommit",
                      note: "", enabled: true }) as any}
      editor={(v, on) => (
        <div class="grid grid-cols-3 gap-2">
          <In  label="AET"  value={v.aet}  onInput={(x) => on({ ...v, aet: x })} />
          <In  label="Host" value={v.host} onInput={(x) => on({ ...v, host: x })} />
          <In  label="Port" value={v.port} type="number" onInput={(x) => on({ ...v, port: parseInt(x,10) || 0 })} />
          <Sel label="Role" value={v.role}
               options={["StorageCommit","MPPS","MWL","QueryRetrieve"]}
               onInput={(x) => on({ ...v, role: x })} />
          <Chk label="TLS"     value={v.tls}     onInput={(x) => on({ ...v, tls: x })} />
          <Chk label="Enabled" value={v.enabled} onInput={(x) => on({ ...v, enabled: x })} />
          <In  label="Note" value={v.note ?? ""} onInput={(x) => on({ ...v, note: x })} />
        </div>
      )}
    />
  );
}
function RetentionSection() {
  return (
    <ConfigSection
      title="Retention policies"
      hint="Lifecycle rules per modality — purge requires approval by default."
      path="retention"
      columns={[
        { key: "name",        label: "Name", mono: true },
        { key: "modality",    label: "Modality" },
        { key: "retain_days", label: "Days", mono: true },
        { key: "tier",        label: "Tier" },
        { key: "purge_requires_approval", label: "Approve purge" },
        { key: "enabled",     label: "Enabled" },
      ]}
      blank={() => ({ name: "", modality: "", retain_days: 2555, tier: "warm",
                      purge_requires_approval: true, note: "", enabled: true }) as any}
      editor={(v, on) => (
        <div class="grid grid-cols-3 gap-2">
          <In  label="Name"     value={v.name}     onInput={(x) => on({ ...v, name: x })} />
          <In  label="Modality" value={v.modality ?? ""} onInput={(x) => on({ ...v, modality: x })}
               placeholder="blank = all" />
          <In  label="Retain (days)" value={v.retain_days} type="number"
               onInput={(x) => on({ ...v, retain_days: parseInt(x, 10) || 0 })} />
          <Sel label="Tier" value={v.tier} options={["hot","warm","cold"]}
               onInput={(x) => on({ ...v, tier: x })} />
          <Chk label="Purge needs approval" value={v.purge_requires_approval}
               onInput={(x) => on({ ...v, purge_requires_approval: x })} />
          <Chk label="Enabled" value={v.enabled} onInput={(x) => on({ ...v, enabled: x })} />
          <In  label="Note" value={v.note ?? ""} onInput={(x) => on({ ...v, note: x })} />
        </div>
      )}
    />
  );
}
function AiModelsSection() {
  return (
    <ConfigSection
      title="AI model inventory"
      hint="Per ISO 13485 §7.5.6 — version, vendor, FDA 510(k) K-number, cleared indication."
      path="models"
      columns={[
        { key: "name",              label: "Name", mono: true },
        { key: "version",           label: "Version", mono: true },
        { key: "vendor",            label: "Vendor" },
        { key: "fda_k_number",      label: "K-number", mono: true },
        { key: "cleared_indication", label: "Indication" },
        { key: "specialty",         label: "Specialty" },
        { key: "enabled",           label: "Enabled" },
      ]}
      blank={() => ({ name: "", vendor: "", version: "",
                      fda_k_number: "", cleared_indication: "",
                      specialty: "pathology", enabled: true,
                      endpoint: "", note: "" }) as any}
      editor={(v, on) => (
        <div class="grid grid-cols-3 gap-2">
          <In  label="Name"     value={v.name}    onInput={(x) => on({ ...v, name: x })} />
          <In  label="Vendor"   value={v.vendor ?? ""} onInput={(x) => on({ ...v, vendor: x })} />
          <In  label="Version"  value={v.version} onInput={(x) => on({ ...v, version: x })} />
          <In  label="FDA 510(k) K-number" value={v.fda_k_number ?? ""} onInput={(x) => on({ ...v, fda_k_number: x })}
               placeholder="K123456" />
          <In  label="Cleared indication"  value={v.cleared_indication ?? ""}
               onInput={(x) => on({ ...v, cleared_indication: x })} />
          <Sel label="Specialty" value={v.specialty ?? "pathology"}
               options={["pathology","radiology","endoscopy","radiotherapy"]}
               onInput={(x) => on({ ...v, specialty: x })} />
          <In  label="Endpoint" value={v.endpoint ?? ""} onInput={(x) => on({ ...v, endpoint: x })}
               placeholder="/viewer-api/ai/…" />
          <Chk label="Enabled" value={v.enabled} onInput={(x) => on({ ...v, enabled: x })} />
          <In  label="Note" value={v.note ?? ""} onInput={(x) => on({ ...v, note: x })} />
        </div>
      )}
    />
  );
}

// ------------------------------------------------------------
//  Access Report (HIPAA §164.308(a)(1)(ii)(D))
//
//  "Who accessed this patient's PHI, when, and what did they do?"
//  The canonical HIPAA audit review surface. Reads the persistent
//  audit_log with patient_mrn filter, groups rows by actor, and
//  exports a signed CSV suitable for compliance review + evidence
//  archiving.
// ------------------------------------------------------------
async function fetchAccessForPatient(mrn: string): Promise<any[]> {
  const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
  const rsp = await fetch(`/viewer-api/audit/_search?patient=${encodeURIComponent(mrn)}&limit=5000`, {
    headers: { Authorization: `Bearer ${auth?.accessToken ?? ""}` },
  });
  if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
  return rsp.json();
}

function AccessReportTab() {
  const [mrn, setMrn] = createSignal("");
  const [submitted, setSubmitted] = createSignal("");
  const rows = createQuery(() => ({
    queryKey: ["access-report", submitted()],
    queryFn:  () => submitted() ? fetchAccessForPatient(submitted()) : Promise.resolve([]),
    enabled:  !!submitted(),
  }));
  function groupByActor(rs: any[]) {
    const map = new Map<string, any[]>();
    for (const r of rs) {
      const key = r.actor_sub ?? "(system)";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    }
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }
  function fmt(iso: string) {
    try { return new Date(iso).toISOString().replace("T"," ").replace(/\..*/, "Z"); }
    catch { return iso; }
  }
  function exportCsv() {
    const rs = rows.data ?? [];
    const hdr = ["patient_mrn","actor_sub","actor_role","occurred_at","kind","object_ref","study_uid","ip","metadata"].join(",");
    const esc = (v: any) => {
      const s = v == null ? "" : String(v);
      return /[,"\r\n]/.test(s) ? `"${s.replace(/"/g,'""')}"` : s;
    };
    const body = rs.map((r: any) =>
      [r.patient_mrn, r.actor_sub, r.actor_role, r.occurred_at, r.kind,
       r.object_ref, r.study_uid, r.ip, JSON.stringify(r.metadata ?? {})].map(esc).join(",")).join("\n");
    const blob = new Blob([hdr + "\n" + body + "\n"], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `access-report-${submitted()}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }
  return (
    <div class="p-4 space-y-3">
      <div class="flex items-end gap-2">
        <label class="flex flex-col gap-0.5">
          <span class="text-[10px] uppercase tracking-wider text-ink-700">Patient MRN</span>
          <input class="rounded border border-ink-300 bg-ink-200 px-2 py-1 font-mono text-[11px]"
                 placeholder="MRN-…"
                 value={mrn()}
                 onInput={(e) => setMrn(e.currentTarget.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") setSubmitted(mrn().trim()); }} />
        </label>
        <button class="btn-primary text-xs" onClick={() => setSubmitted(mrn().trim())}>
          Run report
        </button>
        <Show when={rows.data && rows.data.length > 0}>
          <button class="btn-ghost text-xs" onClick={exportCsv}>Export CSV</button>
        </Show>
        <span class="ml-auto text-[11px] text-ink-700">
          HIPAA §164.308(a)(1)(ii)(D) — periodic review of PHI access.
        </span>
      </div>

      <Show when={rows.error}>
        <div class="rounded border border-danger bg-danger/10 p-2 text-[11px] text-danger">{(rows.error as Error).message}</div>
      </Show>
      <Show when={rows.data && submitted()}>
        <div class="text-[11px] text-ink-700">
          {rows.data!.length} event(s) against MRN{" "}
          <code class="text-white">{submitted()}</code>,{" "}
          {groupByActor(rows.data!).length} distinct actor(s).
        </div>
        <div class="space-y-3">
          <For each={groupByActor(rows.data!)}>{([actor, evts]) => (
            <div class="rounded border border-ink-300 bg-ink-100 p-3">
              <div class="flex items-center gap-2">
                <span class="chip-brand">{actor.length > 20 ? actor.slice(0, 20) + "…" : actor}</span>
                <span class="text-[11px] text-white">{evts.length} event(s)</span>
                <span class="text-[11px] text-ink-700">
                  · {evts[0].actor_role ?? "—"} ·
                  earliest {fmt(evts[evts.length-1].occurred_at)} ·
                  latest {fmt(evts[0].occurred_at)}
                </span>
              </div>
              <table class="mt-2 w-full text-[11px]">
                <thead class="text-[10px] uppercase text-ink-700">
                  <tr><Th>When</Th><Th>Event</Th><Th>Study</Th><Th>Object</Th><Th>IP</Th></tr>
                </thead>
                <tbody>
                  <For each={evts.slice(0, 100)}>{(r: any) => (
                    <tr class="border-t border-ink-300">
                      <Td>{fmt(r.occurred_at)}</Td>
                      <Td><span class="chip-brand !text-[9px]">{r.kind}</span></Td>
                      <Td class="font-mono text-ink-700">{r.study_uid ? r.study_uid.slice(-14) : "—"}</Td>
                      <Td class="text-ink-700 truncate max-w-[200px]" title={r.object_ref}>{r.object_ref}</Td>
                      <Td class="text-ink-700">{r.ip ?? "—"}</Td>
                    </tr>
                  )}</For>
                  <Show when={evts.length > 100}>
                    <tr><td colspan="5" class="p-2 text-center text-ink-700">
                      ({evts.length - 100} more events not shown — use CSV export for full list)
                    </td></tr>
                  </Show>
                </tbody>
              </table>
            </div>
          )}</For>
        </div>
      </Show>
      <Show when={!submitted()}>
        <div class="rounded border border-ink-300 bg-ink-100 p-6 text-center text-[11px] text-ink-700">
          Enter a patient MRN to see every audit event that touched their PHI —
          studies opened, reports signed, shares minted, PDFs exported. Grouped
          by actor so compliance review can spot unexpected access patterns.
        </div>
      </Show>
    </div>
  );
}

// ------------------------------------------------------------
//  Sessions (Phase B.3 — idle-timeout + active-session view)
//
//  Active sessions inferred from the audit log: latest auth.login
//  per actor that has NOT been followed by an auth.logout. Force-
//  logout fires a POST /viewer-api/admin/sessions/{sub}/revoke that
//  calls Zitadel's user/_unlock + token-revoke for the subject.
//  Idle-timeout is a site-wide minutes value held in the viewer
//  config; the SPA reads it on boot and auto-signs-out on inactivity.
// ------------------------------------------------------------
async function fetchSessions(): Promise<any[]> {
  const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
  const rsp = await fetch("/viewer-api/admin/sessions", {
    headers: { Authorization: `Bearer ${auth?.accessToken ?? ""}` },
  });
  if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
  return rsp.json();
}
async function fetchPolicy() {
  const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
  const rsp = await fetch("/viewer-api/admin/policy", {
    headers: { Authorization: `Bearer ${auth?.accessToken ?? ""}` },
  });
  if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
  return rsp.json();
}
async function savePolicy(patch: any) {
  const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
  const rsp = await fetch("/viewer-api/admin/policy", {
    method: "PUT",
    headers: { "content-type": "application/json",
               Authorization: `Bearer ${auth?.accessToken ?? ""}` },
    body: JSON.stringify(patch),
  });
  if (!rsp.ok) throw new Error(`HTTP ${rsp.status} — ${await rsp.text()}`);
  return rsp.json();
}

function SessionsTab() {
  const sessions = createQuery(() => ({
    queryKey: ["admin-sessions"],
    queryFn:  fetchSessions,
    refetchInterval: 30_000,
  }));
  const policy = createQuery(() => ({
    queryKey: ["admin-policy"],
    queryFn:  fetchPolicy,
  }));
  const [form, setForm] = createSignal<any>({});
  const [saving, setSaving] = createSignal(false);
  const [err,    setErr]    = createSignal<string | null>(null);
  createEffect(() => { if (policy.data) setForm(policy.data); });

  async function save() {
    setSaving(true); setErr(null);
    try { await savePolicy(form()); await policy.refetch(); }
    catch (e) { setErr((e as Error).message); }
    finally { setSaving(false); }
  }
  async function forceLogout(sub: string, reason: string) {
    const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
    const rsp = await fetch(`/viewer-api/admin/sessions/${encodeURIComponent(sub)}/revoke`, {
      method: "POST",
      headers: { "content-type": "application/json",
                 Authorization: `Bearer ${auth?.accessToken ?? ""}` },
      body: JSON.stringify({ reason }),
    });
    if (rsp.ok) await sessions.refetch();
  }

  return (
    <div class="p-4 space-y-3">
      <div class="flex items-center gap-2 text-sm">
        <span class="chip-brand">Session policy</span>
        <span class="text-[11px] text-ink-700">
          FDA cybersecurity 2023 — idle-timeout + concurrent-session limit.
        </span>
      </div>
      <Show when={err()}>
        <div class="rounded border border-danger bg-danger/10 p-2 text-[11px] text-danger">{err()}</div>
      </Show>
      <Show when={policy.data}>
        <div class="grid grid-cols-3 gap-3 rounded border border-ink-300 bg-ink-100 p-3">
          <In label="Idle timeout (minutes)"
              value={form().idle_timeout_minutes} type="number"
              onInput={(v) => setForm({ ...form(), idle_timeout_minutes: parseInt(v, 10) || 0 })} />
          <In label="Concurrent sessions per user"
              value={form().concurrent_session_limit} type="number"
              onInput={(v) => setForm({ ...form(), concurrent_session_limit: parseInt(v, 10) || 0 })} />
          <Chk label="Break-glass access requires justification"
               value={!!form().break_glass_enabled}
               onInput={(v) => setForm({ ...form(), break_glass_enabled: v })} />
          <In label="Break-glass roster window (days)"
              value={form().break_glass_roster_days} type="number"
              onInput={(v) => setForm({ ...form(), break_glass_roster_days: parseInt(v, 10) || 0 })} />
          <Chk label="Retention purge requires approval"
               value={!!form().retention_approval_required}
               onInput={(v) => setForm({ ...form(), retention_approval_required: v })} />
          <div class="col-span-3 flex items-center justify-end">
            <button class="btn-primary text-xs" disabled={saving()} onClick={save}>
              {saving() ? "Saving…" : "Save policy"}
            </button>
          </div>
        </div>
      </Show>

      <div class="flex items-center gap-2 text-sm mt-4">
        <span class="chip-brand">Active sessions</span>
        <span class="text-[11px] text-ink-700">
          Last 60 minutes · inferred from auth.login / auth.logout audit pairs.
        </span>
      </div>
      <Show when={sessions.error}>
        <div class="rounded border border-warn bg-warn/10 p-2 text-[11px] text-warn">
          {(sessions.error as Error).message}
        </div>
      </Show>
      <Show when={sessions.data && sessions.data.length === 0}>
        <div class="text-[11px] text-ink-700">No active sessions in the last hour.</div>
      </Show>
      <Show when={sessions.data && sessions.data.length > 0}>
        <div class="overflow-x-auto rounded border border-ink-300">
          <table class="min-w-full text-[11px]">
            <thead class="bg-ink-100 text-[10px] uppercase text-ink-700">
              <tr><Th>Actor</Th><Th>Role</Th><Th>Login</Th><Th>Last activity</Th><Th>IP</Th><Th>UA</Th><Th>Actions</Th></tr>
            </thead>
            <tbody>
              <For each={sessions.data}>{(s: any) => (
                <tr class="border-t border-ink-300">
                  <Td class="font-mono text-white">{s.actor_name ?? (s.actor_sub ?? "").slice(0, 20) + "…"}</Td>
                  <Td class="text-ink-700">{s.actor_role ?? "—"}</Td>
                  <Td class="text-ink-700">{fmtRelative(s.login_at)}</Td>
                  <Td class="text-ink-700">{fmtRelative(s.last_activity_at)}</Td>
                  <Td class="text-ink-700">{s.ip ?? "—"}</Td>
                  <Td class="text-ink-700 truncate max-w-[180px]" title={s.user_agent ?? ""}>{short(s.user_agent ?? "—", 30)}</Td>
                  <Td>
                    <ActionBtn label="Force logout" danger
                               onClick={() => {
                                 const r = prompt("Reason for force-logout (audit-logged):");
                                 if (r !== null) forceLogout(s.actor_sub, r || "");
                               }} />
                  </Td>
                </tr>
              )}</For>
            </tbody>
          </table>
        </div>
      </Show>
    </div>
  );
}
function short(s: string, n: number) { return s.length <= n ? s : s.slice(0, n - 1) + "…"; }

// ------------------------------------------------------------
//  Retention (Phase D.1) — purge request approval queue
// ------------------------------------------------------------
async function fetchRetentionQueue() {
  const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
  const rsp = await fetch(`/viewer-api/admin/retention/requests?status=pending`, {
    headers: { Authorization: `Bearer ${auth?.accessToken ?? ""}` },
  });
  if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
  return rsp.json() as Promise<any[]>;
}
// ------------------------------------------------------------
//  Cases (Phase I — pathology reading queue)
//
//  Admin surface to assign / reassign / reprioritise pathology
//  cases. Mirrors Proscia Concentriq Dx (FDA 510(k) K232897) +
//  Sectra DPath patterns: row-per-case grid with priority +
//  status chips, group-by-MRN expander, bulk-assign modal.
//  Every mutation audit-logged via case.assign / unassign /
//  priority — see admin_audit_evidence §4.13.
// ------------------------------------------------------------
async function fetchCases(filters: Record<string, string>): Promise<any[]> {
  const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
  const params = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([, v]) => v)));
  const rsp = await fetch(`/viewer-api/path/cases?${params}`, {
    headers: { Authorization: `Bearer ${auth?.accessToken ?? ""}` },
  });
  if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
  return rsp.json();
}
async function backfillCases(): Promise<{ inserted: number }> {
  const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
  const rsp = await fetch(`/viewer-api/path/cases/_backfill`, {
    method: "POST",
    headers: { Authorization: `Bearer ${auth?.accessToken ?? ""}` },
  });
  if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
  return rsp.json();
}

function fmtAgeShort(iso: string | null): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60_000);
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

function CaseStatusChip(p: { s: string }) {
  const s = p.s;
  const cls =
      s === "received"  ? "bg-ink-200 text-ink-900"
    : s === "assigned"  ? "bg-brand/20 text-brand"
    : s === "in_review" ? "bg-warn/20 text-warn"
    : s === "reported"  ? "bg-warn/30 text-warn"
    : s === "signed"    ? "bg-ok/20 text-ok"
    : s === "amended"   ? "bg-warn/30 text-warn"
    :                     "bg-ink-200 text-ink-700";
  return <span class={`rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${cls}`}>{s.replace("_", " ")}</span>;
}
function CasePriorityChip(p: { s: string }) {
  if (p.s === "routine") return <span class="text-[9px] text-ink-700">routine</span>;
  const cls = p.s === "stat" ? "bg-danger text-white" : "bg-warn/40 text-warn";
  return <span class={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${cls}`}>{p.s}</span>;
}

function CasesTab() {
  const [statusF,   setStatusF]   = createSignal("active");
  const [priorityF, setPriorityF] = createSignal("");
  const [q,         setQ]         = createSignal("");
  const cases = createQuery(() => ({
    queryKey: ["admin-cases", statusF(), priorityF(), q()],
    queryFn:  () => fetchCases({ status: statusF(), priority: priorityF(), q: q() }),
    refetchInterval: 30_000,
  }));
  const users = createQuery(() => ({
    queryKey: ["admin-users-for-assign"],
    queryFn:  fetchUsers,
  }));
  const [selected, setSelected] = createSignal<Set<string>>(new Set());
  const [assigning, setAssigning] = createSignal(false);
  const [busy, setBusy] = createSignal<string | null>(null);
  const [err,  setErr]  = createSignal<string | null>(null);

  function toggleSelect(case_id: string) {
    const s = new Set(selected());
    if (s.has(case_id)) s.delete(case_id); else s.add(case_id);
    setSelected(s);
  }
  function selectAll() {
    setSelected(new Set((cases.data ?? []).map((c: any) => c.case_id)));
  }
  function clearSelection() { setSelected(new Set()); }

  async function doAssign(pathologist_sub: string, pathologist_name: string, priority: string | null) {
    if (selected().size === 0) return;
    setBusy("assign"); setErr(null);
    try {
      const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
      for (const case_id of selected()) {
        const rsp = await fetch(`/viewer-api/path/cases/${encodeURIComponent(case_id)}/assign`, {
          method: "POST",
          headers: { "content-type": "application/json", Authorization: `Bearer ${auth?.accessToken ?? ""}` },
          body: JSON.stringify({ pathologist_sub, pathologist_name, priority: priority || undefined }),
        });
        if (!rsp.ok) throw new Error(`${case_id}: ${await rsp.text()}`);
      }
      setSelected(new Set());
      setAssigning(false);
      await cases.refetch();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(null); }
  }
  async function doUnassign(case_id: string) {
    setBusy(case_id); setErr(null);
    try {
      const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
      const rsp = await fetch(`/viewer-api/path/cases/${encodeURIComponent(case_id)}/unassign`, {
        method: "POST",
        headers: { Authorization: `Bearer ${auth?.accessToken ?? ""}` },
      });
      if (!rsp.ok) throw new Error(await rsp.text());
      await cases.refetch();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(null); }
  }

  // Top-admin destructive op (Phase J). Soft-delete the underlying
  // study; the case row is hidden via the deleted_at filter on
  // pathCases.list. Recoverable from Admin → Recycle bin within
  // 30 days. CAP COM.40400 + 21 CFR Part 11 §11.10(c) require a
  // written reason — enforced server-side at ≥10 chars.
  async function doDelete(case_id: string) {
    const reason = prompt(
      "DELETE this WSI study?\n\n" +
      "This is a soft delete. The study disappears from clinical reads + the\n" +
      "pathology worklist immediately, but stays recoverable for 30 days from\n" +
      "Admin → Recycle bin. After 30 days the purge sweeper hard-deletes it.\n\n" +
      "Provide a written reason (≥10 chars; audit-logged):",
      ""
    );
    if (!reason || reason.trim().length < 10) {
      if (reason !== null) alert("Reason must be at least 10 characters.");
      return;
    }
    setBusy(case_id + ":delete"); setErr(null);
    try {
      const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
      const rsp = await fetch(`/viewer-api/admin/studies/${encodeURIComponent(case_id)}`, {
        method: "DELETE",
        headers: { "content-type": "application/json", Authorization: `Bearer ${auth?.accessToken ?? ""}` },
        body: JSON.stringify({ reason }),
      });
      if (!rsp.ok) throw new Error(await rsp.text());
      await cases.refetch();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(null); }
  }
  async function doBackfill() {
    setBusy("backfill"); setErr(null);
    try {
      await backfillCases();
      await cases.refetch();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(null); }
  }

  // Reader roles eligible for case assignment under the 4-role
  // matrix: senior + junior (and owner, who can read but normally
  // wouldn't carry a clinical caseload — kept in the candidate list
  // for completeness).
  const pathologists = () =>
    (users.data ?? []).filter((u: any) =>
      u.state === "active" && (
        u.roles?.includes("senior") || u.roles?.includes("junior") || u.roles?.includes("owner")
      ));

  return (
    <div class="p-4 space-y-3">
      <div class="flex flex-wrap items-end gap-2">
        <span class="chip-brand">Cases</span>
        <span class="text-[11px] text-ink-700">
          21 CFR Part 11 §11.50 · CAP COM.40450 — every assignment + reassignment audit-logged with before/after.
        </span>
        <button class="btn-ghost ml-auto text-xs" disabled={busy() === "backfill"} onClick={doBackfill}>
          {busy() === "backfill" ? "Sweeping…" : "Sweep studies → cases"}
        </button>
      </div>

      <div class="flex flex-wrap items-end gap-2 rounded border border-ink-300 bg-ink-100 p-2">
        <label class="flex flex-col gap-0.5">
          <span class="text-[10px] uppercase tracking-wider text-ink-700">Status</span>
          <select class="rounded border border-ink-300 bg-ink-200 px-2 py-1 text-[11px]"
                  value={statusF()} onChange={(e) => setStatusF(e.currentTarget.value)}>
            <option value="active">active (not signed)</option>
            <option value="">all</option>
            <option value="received">received</option>
            <option value="assigned">assigned</option>
            <option value="in_review">in review</option>
            <option value="reported">reported</option>
            <option value="signed">signed</option>
            <option value="amended">amended</option>
          </select>
        </label>
        <label class="flex flex-col gap-0.5">
          <span class="text-[10px] uppercase tracking-wider text-ink-700">Priority</span>
          <select class="rounded border border-ink-300 bg-ink-200 px-2 py-1 text-[11px]"
                  value={priorityF()} onChange={(e) => setPriorityF(e.currentTarget.value)}>
            <option value="">any</option>
            <option value="stat">stat</option>
            <option value="urgent">urgent</option>
            <option value="routine">routine</option>
          </select>
        </label>
        <label class="flex flex-col gap-0.5">
          <span class="text-[10px] uppercase tracking-wider text-ink-700">Search (MRN, accession, name)</span>
          <input class="rounded border border-ink-300 bg-ink-200 px-2 py-1 font-mono text-[11px]"
                 value={q()}
                 onInput={(e) => setQ(e.currentTarget.value)} />
        </label>
        <Show when={selected().size > 0}>
          <button class="btn-primary text-xs" onClick={() => setAssigning(true)}>
            Assign {selected().size} case(s)…
          </button>
          <button class="btn-ghost text-xs" onClick={clearSelection}>Clear</button>
        </Show>
        <Show when={(cases.data?.length ?? 0) > 0}>
          <button class="btn-ghost text-xs" onClick={selectAll}>Select all</button>
        </Show>
      </div>

      <Show when={err()}>
        <div class="rounded border border-danger bg-danger/10 p-2 text-[11px] text-danger">{err()}</div>
      </Show>

      <Show when={cases.data && cases.data.length === 0}>
        <div class="rounded border border-ink-300 bg-ink-100 p-6 text-center text-[11px] text-ink-700">
          No cases match. Pathology cases enter the queue once their slides are STOW-ingested
          and a path_cases row exists. Use <b>Sweep</b> if you've just imported studies.
        </div>
      </Show>

      <Show when={cases.data && cases.data.length > 0}>
        <div class="overflow-x-auto rounded border border-ink-300">
          <table class="min-w-full text-[11px]">
            <thead class="bg-ink-100 text-[10px] uppercase tracking-wider text-ink-700">
              <tr>
                <Th></Th><Th>Status</Th><Th>Pri</Th><Th>MRN</Th><Th>Patient</Th>
                <Th>Accession / Case</Th><Th>Slides·Blocks</Th>
                <Th>Assignee</Th><Th>Received</Th><Th>Last event</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              <For each={cases.data}>{(c: any) => (
                <tr class="border-t border-ink-300 hover:bg-ink-200/30">
                  <Td>
                    <input type="checkbox" checked={selected().has(c.case_id)}
                           onChange={() => toggleSelect(c.case_id)} />
                  </Td>
                  <Td><CaseStatusChip s={c.status} /></Td>
                  <Td><CasePriorityChip s={c.priority} /></Td>
                  <Td class="font-mono text-white">{c.mrn}</Td>
                  <Td>{c.patient_name ?? "—"}</Td>
                  <Td class="font-mono text-ink-700" title={c.case_id}>
                    {c.accession ?? c.case_id.slice(-12)}
                  </Td>
                  <Td class="font-mono">{c.slide_count ?? 0} · {c.block_count ?? 0}</Td>
                  <Td>
                    <Show when={c.assigned_to_name || c.assigned_to} fallback={<span class="text-ink-700">—</span>}>
                      <span class="text-white">{c.assigned_to_name ?? c.assigned_to.slice(0, 12)}</span>
                    </Show>
                  </Td>
                  <Td class="text-ink-700">{fmtAgeShort(c.received_at)} ago</Td>
                  <Td class="text-ink-700">{fmtAgeShort(c.last_event_at)} ago</Td>
                  <Td>
                    <div class="flex gap-1">
                      <Show when={c.assigned_to && c.status !== "signed"}>
                        <ActionBtn label="Unassign"
                                   onClick={() => doUnassign(c.case_id)}
                                   busy={busy() === c.case_id} />
                      </Show>
                      <ActionBtn label="Delete" danger
                                 onClick={() => doDelete(c.case_id)}
                                 busy={busy() === c.case_id + ":delete"} />
                    </div>
                  </Td>
                </tr>
              )}</For>
            </tbody>
          </table>
        </div>
      </Show>

      <Show when={assigning()}>
        <Modal onClose={() => setAssigning(false)}>
          <h2 class="text-base font-semibold text-white">Assign {selected().size} case(s)</h2>
          <div class="mt-2 text-[11px] text-ink-700">
            Eligible readers below are users with the
            <code class="mx-1">senior</code> or <code class="mx-1">junior</code>
            role active in this product's identity provider.
          </div>
          <Show when={!users.data}>
            <div class="mt-2 text-[11px] text-ink-700">loading users…</div>
          </Show>
          <Show when={users.data && pathologists().length === 0}>
            <div class="mt-2 text-[11px] text-warn">
              No active readers found. Grant <code>senior</code> or
              <code>junior</code> to a user under Admin → Users first.
            </div>
          </Show>
          <Show when={pathologists().length > 0}>
            <div class="mt-3 flex flex-col gap-1">
              <For each={pathologists()}>{(u: any) => (
                <button type="button"
                        class="flex items-center gap-2 rounded border border-ink-300 px-2 py-1.5 text-left text-[11px] hover:border-brand"
                        disabled={busy() === "assign"}
                        onClick={() => doAssign(u.id, [u.firstName, u.lastName].filter(Boolean).join(" ") || u.userName, null)}>
                  <span class="font-mono text-white">{u.userName}</span>
                  <span class="text-ink-700">· {[u.firstName, u.lastName].filter(Boolean).join(" ") || "—"}</span>
                  <span class="ml-auto text-[9px] text-ink-700">{u.email}</span>
                </button>
              )}</For>
            </div>
          </Show>
        </Modal>
      </Show>
    </div>
  );
}

// ------------------------------------------------------------
//  Recycle bin (Phase J — soft-deleted WSI studies)
// ------------------------------------------------------------
async function fetchDeleted(): Promise<any[]> {
  const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
  const rsp = await fetch("/viewer-api/admin/studies/deleted", {
    headers: { Authorization: `Bearer ${auth?.accessToken ?? ""}` },
  });
  if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
  return rsp.json();
}
function RecycleBinTab() {
  const rows = createQuery(() => ({
    queryKey: ["admin-recycle"],
    queryFn:  fetchDeleted,
    refetchInterval: 60_000,
  }));
  const [busy, setBusy] = createSignal<string | null>(null);
  const [err,  setErr]  = createSignal<string | null>(null);
  const [note, setNote] = createSignal<string | null>(null);

  async function restore(uid: string) {
    const reason = prompt("Reason for restoring this study (≥10 chars, audit-logged):", "");
    if (!reason || reason.trim().length < 10) return;
    setBusy(uid + ":restore"); setErr(null); setNote(null);
    try {
      const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
      const rsp = await fetch(`/viewer-api/admin/studies/${encodeURIComponent(uid)}/restore`, {
        method: "POST",
        headers: { "content-type": "application/json", Authorization: `Bearer ${auth?.accessToken ?? ""}` },
        body: JSON.stringify({ reason }),
      });
      if (!rsp.ok) throw new Error(await rsp.text());
      setNote("Study restored.");
      await rows.refetch();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(null); }
  }
  async function purgeNow() {
    // Pre-count how many of the soft-deleted rows are actually past
    // their 30-day window so the operator knows what to expect from
    // the click — avoids the "I clicked Run purge sweep and nothing
    // happened" confusion when the answer is "0 rows are eligible".
    const all = rows.data ?? [];
    const due = all.filter((r: any) => {
      if (!r.purge_scheduled_at) return false;
      return new Date(r.purge_scheduled_at).getTime() <= Date.now();
    }).length;
    const remaining = all.length - due;
    const msg = due > 0
      ? `Hard-delete ${due} stud${due === 1 ? "y" : "ies"} past their 30-day recovery window? This cannot be undone. Audit-logged.`
      : `0 studies are past their 30-day recovery window — running the sweep will be a no-op.\n\n${all.length} stud${all.length === 1 ? "y" : "ies"} still in the recovery window.\n\nProceed anyway?`;
    if (!confirm(msg)) return;
    setBusy("purge"); setErr(null); setNote(null);
    try {
      const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
      const rsp = await fetch("/viewer-api/admin/studies/_purge_due", {
        method: "POST", headers: { Authorization: `Bearer ${auth?.accessToken ?? ""}` },
      });
      if (!rsp.ok) throw new Error(await rsp.text());
      const j = await rsp.json() as { purged: number };
      setNote(j.purged > 0
        ? `Purge sweep ran — ${j.purged} stud${j.purged === 1 ? "y" : "ies"} hard-deleted (${remaining} still in the recovery window).`
        : `Purge sweep ran — 0 studies hard-deleted (none past their 30-day window).  ${all.length} still in Recycle bin.`);
      await rows.refetch();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(null); }
  }

  return (
    <div class="p-4 space-y-3">
      <div class="flex items-center gap-2 text-sm">
        <span class="chip-brand">Recycle bin</span>
        <span class="text-[11px] text-ink-700">
          21 CFR Part 11 §11.10(c) · HIPAA §164.310(d)(2)(i) · CAP COM.40400 — soft-delete with 30-day recovery window.
        </span>
        <button class="btn-ghost ml-auto text-xs" disabled={busy() === "purge"} onClick={purgeNow}>
          {busy() === "purge" ? "Purging…" : "Run purge sweep"}
        </button>
      </div>
      <Show when={err()}>
        <div class="rounded border border-danger bg-danger/10 p-2 text-[11px] text-danger">{err()}</div>
      </Show>
      <Show when={note()}>
        <div class="rounded border border-ok bg-ok/10 p-2 text-[11px] text-ok">{note()}</div>
      </Show>
      <Show when={rows.data && rows.data.length === 0}>
        <div class="rounded border border-ink-300 bg-ink-100 p-6 text-center text-[11px] text-ink-700">
          No soft-deleted studies. Studies move here when an admin uses
          <b> Admin → Cases → … → Delete</b> (or DICOM-list-page row delete).
          Hard-delete runs automatically when the 30-day window expires;
          the audit trail is permanent regardless.
        </div>
      </Show>
      <Show when={rows.data && rows.data.length > 0}>
        <div class="overflow-x-auto rounded border border-ink-300">
          <table class="min-w-full text-[11px]">
            <thead class="bg-ink-100 text-[10px] uppercase tracking-wider text-ink-700">
              <tr>
                <Th>Study</Th><Th>MRN</Th><Th>Modality</Th><Th>Deleted</Th><Th>By</Th>
                <Th>Reason</Th><Th>Purge in</Th><Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              <For each={rows.data}>{(r: any) => (
                <tr class="border-t border-ink-300">
                  <Td class="font-mono text-ink-700" title={r.study_uid}>
                    {(r.accession ?? r.study_uid).slice(0, 18)}
                  </Td>
                  <Td>{r.mrn}</Td>
                  <Td class="text-ink-700">{r.modality}</Td>
                  <Td class="text-ink-700">{fmtRelative(r.deleted_at)}</Td>
                  <Td class="font-mono text-ink-700" title={r.deleted_by}>
                    {(r.deleted_by ?? "").slice(0, 14)}
                  </Td>
                  <Td class="max-w-[280px]" title={r.delete_reason}>
                    <span class="block truncate">{r.delete_reason}</span>
                  </Td>
                  <Td class="text-ink-700">
                    <Show when={r.purge_scheduled_at}>
                      {(() => {
                        const ms = new Date(r.purge_scheduled_at).getTime() - Date.now();
                        const d = Math.round(ms / 86_400_000);
                        return d > 0 ? `${d}d` : "OVERDUE";
                      })()}
                    </Show>
                  </Td>
                  <Td>
                    <ActionBtn label="Restore"
                               onClick={() => restore(r.study_uid)}
                               busy={busy() === r.study_uid + ":restore"} />
                  </Td>
                </tr>
              )}</For>
            </tbody>
          </table>
        </div>
      </Show>
      <div class="text-[11px] leading-relaxed text-ink-700">
        Soft-deleted studies are hidden from QIDO / WADO / pathology
        worklist. The hash-chained <code>study.soft_delete</code> row
        records the deleter + reason + UTC timestamp; restoring emits
        <code class="ml-1">study.restore</code>; the eventual hard-delete
        emits <code>study.purge_exec</code>. The audit trail itself is
        never deleted (append-only `audit_log`).
      </div>
    </div>
  );
}

function RetentionTab() {
  const queue = createQuery(() => ({
    queryKey: ["retention-queue"],
    queryFn:  fetchRetentionQueue,
    refetchInterval: 60_000,
  }));
  const [busy, setBusy] = createSignal<string | null>(null);
  const [err,  setErr]  = createSignal<string | null>(null);

  async function doAction(id: number, action: "approve" | "reject", reason: string) {
    setBusy(id + ":" + action); setErr(null);
    try {
      const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
      const rsp = await fetch(`/viewer-api/admin/retention/requests/${id}/${action}`, {
        method: "POST",
        headers: { "content-type": "application/json",
                   Authorization: `Bearer ${auth?.accessToken ?? ""}` },
        body: JSON.stringify({ reason }),
      });
      if (!rsp.ok) throw new Error(await rsp.text());
      await queue.refetch();
    } catch (e) { setErr((e as Error).message); }
    finally    { setBusy(null); }
  }
  async function sweepNow() {
    setBusy("sweep"); setErr(null);
    try {
      const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
      const rsp = await fetch(`/viewer-api/admin/retention/sweep`, {
        method: "POST",
        headers: { Authorization: `Bearer ${auth?.accessToken ?? ""}` },
      });
      if (!rsp.ok) throw new Error(await rsp.text());
      await queue.refetch();
    } catch (e) { setErr((e as Error).message); }
    finally    { setBusy(null); }
  }
  async function executeApproved() {
    if (!confirm("Execute every APPROVED request? Soft-delete today — audit rows only, no object-storage touch. Audit-logged.")) return;
    setBusy("execute"); setErr(null);
    try {
      const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
      const rsp = await fetch(`/viewer-api/admin/retention/execute`, {
        method: "POST",
        headers: { Authorization: `Bearer ${auth?.accessToken ?? ""}` },
      });
      if (!rsp.ok) throw new Error(await rsp.text());
      await queue.refetch();
    } catch (e) { setErr((e as Error).message); }
    finally    { setBusy(null); }
  }

  return (
    <div class="p-4 space-y-3">
      <div class="flex items-center gap-2 text-sm">
        <span class="chip-brand">Retention queue</span>
        <span class="text-[11px] text-ink-700">
          21 CFR Part 11 §11.10(c) · HIPAA §164.312(c)(1) — every destructive PHI op approved + audit-logged.
        </span>
        <button class="btn-ghost ml-auto text-xs" disabled={busy() === "sweep"}
                onClick={sweepNow}>
          {busy() === "sweep" ? "Sweeping…" : "Run sweeper now"}
        </button>
        <button class="btn-ghost text-xs" disabled={busy() === "execute"}
                onClick={executeApproved}>
          {busy() === "execute" ? "Executing…" : "Execute approved"}
        </button>
      </div>
      <Show when={err()}>
        <div class="rounded border border-danger bg-danger/10 p-2 text-[11px] text-danger">{err()}</div>
      </Show>
      <Show when={queue.data && queue.data.length === 0}>
        <div class="rounded border border-ink-300 bg-ink-100 p-6 text-center text-[11px] text-ink-700">
          No pending retention requests. The sweeper runs every 6 hours; use
          "Run sweeper now" to scan on demand.
        </div>
      </Show>
      <Show when={queue.data && queue.data.length > 0}>
        <div class="overflow-x-auto rounded border border-ink-300">
          <table class="min-w-full text-[11px]">
            <thead class="bg-ink-100 text-[10px] uppercase text-ink-700">
              <tr>
                <Th>#</Th><Th>Requested</Th><Th>Study</Th><Th>Patient MRN</Th>
                <Th>Modality</Th><Th>Age (d)</Th><Th>Reason</Th><Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              <For each={queue.data}>{(r: any) => (
                <tr class="border-t border-ink-300">
                  <Td class="font-mono text-ink-700">{r.id}</Td>
                  <Td class="text-ink-700">{fmtRelative(r.requested_at)}</Td>
                  <Td class="font-mono text-ink-700" title={r.study_uid}>{short(r.study_uid, 18)}</Td>
                  <Td>{r.patient_mrn ?? "—"}</Td>
                  <Td class="text-ink-700">{r.modality ?? "—"}</Td>
                  <Td class="font-mono">{r.age_days}</Td>
                  <Td><span class="chip-warn !text-[9px]">{r.reason}</span></Td>
                  <Td>
                    <div class="flex gap-1">
                      <ActionBtn label="Approve"
                                 onClick={() => {
                                   const reason = prompt(`Approval reason? (audit-logged)`, "");
                                   if (reason !== null) doAction(r.id, "approve", reason);
                                 }}
                                 busy={busy() === `${r.id}:approve`} />
                      <ActionBtn label="Reject" danger
                                 onClick={() => {
                                   const reason = prompt(`Rejection reason? (audit-logged)`, "");
                                   if (reason !== null) doAction(r.id, "reject", reason);
                                 }}
                                 busy={busy() === `${r.id}:reject`} />
                    </div>
                  </Td>
                </tr>
              )}</For>
            </tbody>
          </table>
        </div>
      </Show>
      <div class="text-[11px] leading-relaxed text-ink-700">
        Approval grants permission to purge but does NOT execute the
        delete. Click <b>Execute approved</b> to run every approved
        request. Today's executor is a SOFT delete — it emits
        <code>system.retention.purge_exec</code> audit rows but leaves
        object storage untouched, so the chain of custody is present
        before production-grade deletion is signed off clinically.
      </div>
    </div>
  );
}

// ------------------------------------------------------------
//  Shared bits
// ------------------------------------------------------------
function Th(p: { children: any }) {
  return <th class="px-3 py-2 text-left text-[10px] font-semibold">{p.children}</th>;
}
function Td(p: { children: any; class?: string }) {
  return <td class={"px-3 py-2 align-middle " + (p.class ?? "")}>{p.children}</td>;
}
function fmtRelative(iso: string): string {
  const d = new Date(iso).getTime();
  const now = Date.now();
  const mins = Math.round((now - d) / 60_000);
  if (mins < 1)     return "just now";
  if (mins < 60)    return `${mins} min ago`;
  if (mins < 24*60) return `${Math.round(mins / 60)} h ago`;
  return `${Math.round(mins / (60 * 24))} d ago`;
}

// ─────────────────────────────────────────────────────────────────
// Desktop Licenses tab
// Issue / list / drill-down / revoke per-machine licenses for the
// medpod Endopod desktop recorder. See
// endopod/docs/desktop-edge/licensing.md for the full design.
// ─────────────────────────────────────────────────────────────────

interface LicenseRow {
  license_id: string;
  first_segment: string;
  tier: "standard" | "pro";
  status: "issued" | "activated" | "revoked" | "expired";
  site_name: string | null;
  notes: string | null;
  created_by: string;
  created_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  revoke_reason: string | null;
  active_seats: number;
}
interface ActivationRow {
  activation_id: string;
  hardware_id_hash: string;
  app_version: string | null;
  activation_ip: string | null;
  activated_at: string;
  last_seen_at: string;
  deactivated_at: string | null;
  deactivation_reason: string | null;
}
interface IssueResponse { license_id: string; key: string; }

function DesktopLicensesTab() {
  const [rows, setRows]     = createSignal<LicenseRow[]>([]);
  const [loading, setLoad]  = createSignal(false);
  const [showIssue, setShowIssue] = createSignal(false);
  const [justIssued, setJustIssued] = createSignal<IssueResponse | null>(null);
  const [drilldown, setDrilldown] = createSignal<{ license: LicenseRow; activations: ActivationRow[] } | null>(null);
  const [error, setError]   = createSignal<string | null>(null);

  async function load() {
    setLoad(true); setError(null);
    try {
      const a = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
      const rsp = await fetch("/viewer-api/admin/desktop-licenses", {
        headers: { Authorization: `Bearer ${a?.accessToken ?? ""}` },
      });
      if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
      setRows(await rsp.json());
    } catch (e) { setError((e as Error).message); }
    setLoad(false);
  }
  onMount(load);

  return (
    <div class="flex h-full flex-col">
      <header class="flex items-center gap-3 border-b border-ink-300 bg-ink-100 px-4 py-2">
        <span class="chip-brand">Desktop licenses</span>
        <span class="text-[11px] text-ink-700">
          One license per recorder workstation · 21 CFR Part 11 §11.10(d) · Minecraft-style first-run activation
        </span>
        <button class="ml-auto rounded border border-brand bg-brand px-3 py-1 text-[11px] font-medium text-white hover:bg-brand-dark"
                onClick={() => setShowIssue(true)}>
          Issue new license
        </button>
        <button class="rounded border border-ink-300 bg-ink-200 px-3 py-1 text-[11px] text-ink-900 hover:bg-ink-100"
                onClick={load}>Refresh</button>
      </header>

      <Show when={error()}>
        <div class="m-4 rounded border border-danger/50 bg-danger/10 p-3 text-[11px] text-danger">
          {error()}
        </div>
      </Show>

      <div class="flex-1 overflow-auto">
        <table class="w-full border-collapse text-[11px]">
          <thead class="sticky top-0 z-10 bg-ink-100 text-left text-[10px] uppercase tracking-wider text-ink-700">
            <tr>
              <Th>Key prefix</Th><Th>Tier</Th><Th>Status</Th><Th>Site</Th>
              <Th>Issued by</Th><Th>Issued at</Th><Th>Expires</Th>
              <Th>Active seats</Th><Th class="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            <Show when={loading()}>
              <tr><td colspan="9" class="p-4 text-center text-ink-700">loading…</td></tr>
            </Show>
            <Show when={!loading() && rows().length === 0}>
              <tr><td colspan="9" class="p-6 text-center text-[11px] text-ink-700">
                No desktop licenses issued yet. Click <em>Issue new license</em> to mint one — keys are shown once at issuance.
              </td></tr>
            </Show>
            <For each={rows()}>{(r) => (
              <tr class="border-b border-ink-300 hover:bg-ink-200/30 cursor-pointer"
                  onClick={() => openDrilldown(r, setDrilldown)}>
                <td class="px-3 py-2 font-mono text-ink-900">{r.first_segment}-…</td>
                <td class="px-3 py-2">
                  <span class="rounded border border-ink-300 bg-ink-50 px-1.5 py-0.5 text-[9px] uppercase tracking-wider">
                    {r.tier}
                  </span>
                </td>
                <td class="px-3 py-2">
                  <span class="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider"
                        classList={{
                          "bg-ink-200 text-ink-800 ring-1 ring-ink-300": r.status === "issued",
                          "bg-brand/15 text-brand ring-1 ring-brand":    r.status === "activated",
                          "bg-danger/15 text-danger ring-1 ring-danger": r.status === "revoked",
                          "bg-warn/15 text-warn ring-1 ring-warn":       r.status === "expired",
                        }}>
                    {r.status}
                  </span>
                </td>
                <td class="px-3 py-2 text-ink-900">{r.site_name ?? "—"}</td>
                <td class="px-3 py-2 font-mono text-ink-700">{r.created_by.slice(0, 8)}…</td>
                <td class="px-3 py-2 text-ink-700">{new Date(r.created_at).toLocaleString()}</td>
                <td class="px-3 py-2 text-ink-700">{r.expires_at ? new Date(r.expires_at).toLocaleString() : "—"}</td>
                <td class="px-3 py-2 font-mono text-ink-900">{r.active_seats}</td>
                <td class="px-3 py-2 text-right">
                  <Show when={r.status !== "revoked"}>
                    <button class="rounded border border-danger px-2 py-0.5 text-[10px] text-danger hover:bg-danger hover:text-white"
                            onClick={(e) => {
                              e.stopPropagation();
                              const reason = prompt("Revoke reason (logged in audit_log):");
                              if (reason) doRevoke(r.license_id, reason, load, setError);
                            }}>
                      Revoke
                    </button>
                  </Show>
                </td>
              </tr>
            )}</For>
          </tbody>
        </table>
      </div>

      <Show when={showIssue()}>
        <IssueLicenseModal
          onClose={() => setShowIssue(false)}
          onIssued={(r) => { setShowIssue(false); setJustIssued(r); load(); }}
        />
      </Show>
      <Show when={justIssued()}>
        <NewKeyRevealModal license={justIssued()!} onClose={() => setJustIssued(null)} />
      </Show>
      <Show when={drilldown()}>
        <DrilldownModal data={drilldown()!} onClose={() => setDrilldown(null)} />
      </Show>
    </div>
  );
}

async function openDrilldown(
  r: LicenseRow,
  setDrilldown: (d: { license: LicenseRow; activations: ActivationRow[] } | null) => void,
): Promise<void> {
  const a = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
  const rsp = await fetch(`/viewer-api/admin/desktop-licenses/${r.license_id}`, {
    headers: { Authorization: `Bearer ${a?.accessToken ?? ""}` },
  });
  if (!rsp.ok) return;
  const d = await rsp.json() as { license: any; activations: ActivationRow[] };
  setDrilldown({ license: r, activations: d.activations });
}
async function doRevoke(
  license_id: string, reason: string, reload: () => void, setError: (s: string | null) => void,
): Promise<void> {
  const a = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
  const rsp = await fetch(`/viewer-api/admin/desktop-licenses/${license_id}/revoke`, {
    method: "POST",
    headers: { Authorization: `Bearer ${a?.accessToken ?? ""}`, "content-type": "application/json" },
    body: JSON.stringify({ reason }),
  });
  if (!rsp.ok) { setError(`revoke failed: HTTP ${rsp.status}`); return; }
  reload();
}

function IssueLicenseModal(p: {
  onClose: () => void;
  onIssued: (r: IssueResponse) => void;
}) {
  const [tier, setTier]       = createSignal<"standard" | "pro">("standard");
  const [siteName, setSite]   = createSignal("");
  const [notes, setNotes]     = createSignal("");
  const [expiry, setExpiry]   = createSignal("");
  const [working, setWorking] = createSignal(false);
  const [error, setError]     = createSignal<string | null>(null);

  async function submit() {
    setWorking(true); setError(null);
    try {
      const a = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
      const rsp = await fetch("/viewer-api/admin/desktop-licenses", {
        method: "POST",
        headers: { Authorization: `Bearer ${a?.accessToken ?? ""}`, "content-type": "application/json" },
        body: JSON.stringify({
          tier: tier(),
          site_name: siteName() || null,
          notes:     notes()    || null,
          expires_at: expiry()  || null,
        }),
      });
      if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
      p.onIssued(await rsp.json());
    } catch (e) { setError((e as Error).message); }
    setWorking(false);
  }

  return (
    <ModalShell title="Issue desktop license" onClose={p.onClose}>
      <div class="space-y-3 text-[11px]">
        <LicField label="Tier">
          <select class="w-full rounded border border-ink-300 bg-ink-100 px-2 py-1 text-[11px] text-ink-900"
                  value={tier()}
                  onChange={(e) => setTier((e.currentTarget as HTMLSelectElement).value as any)}>
            <option value="standard">standard — single seat</option>
            <option value="pro">pro — same as standard for v1; reserved for future seat-aggregation tiers</option>
          </select>
        </LicField>
        <LicField label="Site name">
          <input type="text" class="w-full rounded border border-ink-300 bg-ink-100 px-2 py-1 text-[11px] text-ink-900"
                 placeholder="e.g. Endoscopy Suite 3, Mercy West"
                 value={siteName()}
                 onInput={(e) => setSite((e.currentTarget as HTMLInputElement).value)} />
        </LicField>
        <LicField label="Expires (optional)">
          <input type="date" class="w-full rounded border border-ink-300 bg-ink-100 px-2 py-1 text-[11px] text-ink-900"
                 value={expiry()}
                 onInput={(e) => setExpiry((e.currentTarget as HTMLInputElement).value)} />
        </LicField>
        <LicField label="Notes">
          <textarea class="w-full resize-y rounded border border-ink-300 bg-ink-100 px-2 py-1 text-[11px] text-ink-900"
                    rows="2"
                    placeholder="PO number, contact name, notes for the audit trail"
                    value={notes()}
                    onInput={(e) => setNotes((e.currentTarget as HTMLTextAreaElement).value)} />
        </LicField>
        <Show when={error()}>
          <div class="rounded border border-danger/50 bg-danger/10 p-2 text-[10px] text-danger">{error()}</div>
        </Show>
        <div class="flex justify-end gap-2 border-t border-ink-300 pt-3">
          <button class="rounded border border-ink-300 bg-ink-200 px-3 py-1 text-[11px] text-ink-900 hover:bg-ink-100"
                  onClick={p.onClose}>Cancel</button>
          <button class="rounded border border-brand bg-brand px-3 py-1 text-[11px] font-medium text-white hover:bg-brand-dark"
                  disabled={working()}
                  onClick={submit}>
            {working() ? "issuing…" : "Issue license"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function NewKeyRevealModal(p: { license: IssueResponse; onClose: () => void }) {
  const [copied, setCopied] = createSignal(false);
  function copy() {
    navigator.clipboard.writeText(p.license.key).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }
  return (
    <ModalShell title="License key — shown once" onClose={p.onClose}>
      <div class="space-y-3 text-[11px]">
        <div class="rounded border border-warn bg-warn/10 p-2 text-[10px] text-warn">
          ⚠ Copy this key now. We store only its argon2id hash — you cannot retrieve it again.
          Hand it to the desktop technician for first-run activation.
        </div>
        <div class="rounded border border-ink-300 bg-black/40 p-3 text-center font-mono text-base text-brand">
          {p.license.key}
        </div>
        <div class="flex justify-between text-[10px] text-ink-700">
          <span>license_id <span class="font-mono">{p.license.license_id}</span></span>
          <button class="rounded border border-brand px-2 py-0.5 text-brand hover:bg-brand hover:text-white"
                  onClick={copy}>
            {copied() ? "✓ copied" : "Copy key"}
          </button>
        </div>
        <div class="flex justify-end border-t border-ink-300 pt-3">
          <button class="rounded border border-ink-300 bg-ink-200 px-3 py-1 text-[11px] text-ink-900 hover:bg-ink-100"
                  onClick={p.onClose}>Close (key dismissed)</button>
        </div>
      </div>
    </ModalShell>
  );
}

function DrilldownModal(p: {
  data: { license: LicenseRow; activations: ActivationRow[] };
  onClose: () => void;
}) {
  return (
    <ModalShell title={`License ${p.data.license.first_segment}-… · ${p.data.license.tier}`} onClose={p.onClose}>
      <div class="space-y-3 text-[11px]">
        <div class="rounded border border-ink-300 bg-ink-200/50 p-2 font-mono text-[10px] text-ink-700 break-all">
          license_id {p.data.license.license_id}
        </div>
        <div class="grid grid-cols-2 gap-2 text-[10px]">
          <LicMeta label="Status" value={p.data.license.status} />
          <LicMeta label="Site" value={p.data.license.site_name ?? "—"} />
          <LicMeta label="Issued by" value={p.data.license.created_by} />
          <LicMeta label="Issued at" value={new Date(p.data.license.created_at).toLocaleString()} />
          <LicMeta label="Expires" value={p.data.license.expires_at ? new Date(p.data.license.expires_at).toLocaleString() : "—"} />
          <LicMeta label="Active seats" value={String(p.data.license.active_seats)} />
        </div>
        <Show when={p.data.license.notes}>
          <div class="rounded border border-ink-300 bg-ink-200/50 p-2 text-[10px] text-ink-900 whitespace-pre-wrap">
            {p.data.license.notes}
          </div>
        </Show>
        <Show when={p.data.license.status === "revoked"}>
          <div class="rounded border border-danger/50 bg-danger/10 p-2 text-[10px] text-danger">
            <div class="font-medium">Revoked: {new Date(p.data.license.revoked_at!).toLocaleString()}</div>
            <div class="mt-1">{p.data.license.revoke_reason}</div>
          </div>
        </Show>
        <div>
          <div class="mb-1 text-[10px] uppercase tracking-wider text-ink-700">
            Activations ({p.data.activations.length})
          </div>
          <Show when={p.data.activations.length === 0} fallback={null}>
            <div class="rounded border border-ink-300 bg-ink-200/50 p-2 text-[10px] text-ink-700">
              No activations yet — desktop has not contacted /api/license/activate.
            </div>
          </Show>
          <For each={p.data.activations}>{(a) => (
            <div class="mb-1 rounded border border-ink-300 bg-ink-200/30 p-2 text-[10px]">
              <div class="flex items-center gap-2">
                <span class="font-mono text-ink-900">{a.hardware_id_hash.slice(0, 16)}…</span>
                <Show when={a.deactivated_at} fallback={
                  <span class="rounded-full bg-brand/15 px-1.5 py-0.5 text-[9px] text-brand">live</span>
                }>
                  <span class="rounded-full bg-ink-300 px-1.5 py-0.5 text-[9px] text-ink-700">deactivated</span>
                </Show>
                <span class="ml-auto text-ink-700">{a.app_version ?? "(unknown version)"}</span>
              </div>
              <div class="mt-1 text-ink-700">
                activated {new Date(a.activated_at).toLocaleString()} · last seen {new Date(a.last_seen_at).toLocaleString()}
                <Show when={a.activation_ip}>
                  <span> · ip <span class="font-mono">{a.activation_ip}</span></span>
                </Show>
              </div>
              <Show when={a.deactivated_at}>
                <div class="mt-0.5 text-ink-700">
                  deactivated {new Date(a.deactivated_at!).toLocaleString()} — {a.deactivation_reason}
                </div>
              </Show>
            </div>
          )}</For>
        </div>
        <div class="flex justify-end border-t border-ink-300 pt-3">
          <button class="rounded border border-ink-300 bg-ink-200 px-3 py-1 text-[11px] text-ink-900 hover:bg-ink-100"
                  onClick={p.onClose}>Close</button>
        </div>
      </div>
    </ModalShell>
  );
}

function ModalShell(p: { title: string; children: any; onClose: () => void }) {
  return (
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6"
         onClick={(e) => { if (e.target === e.currentTarget) p.onClose(); }}>
      <div class="w-[520px] max-w-full rounded-lg border border-ink-300 bg-ink-100 p-4 shadow-2xl">
        <div class="mb-3 flex items-baseline justify-between border-b border-ink-300 pb-2">
          <span class="text-[12px] font-semibold uppercase tracking-wider text-white">{p.title}</span>
          <button class="text-ink-700 hover:text-white" onClick={p.onClose}>✕</button>
        </div>
        {p.children}
      </div>
    </div>
  );
}

function LicField(p: { label: string; children: any }) {
  return (
    <div>
      <div class="mb-0.5 text-[10px] uppercase tracking-wider text-ink-700">{p.label}</div>
      {p.children}
    </div>
  );
}
function LicMeta(p: { label: string; value: string }) {
  return (
    <div class="rounded border border-ink-300 bg-ink-200/30 p-1.5">
      <div class="text-[9px] uppercase tracking-wider text-ink-700">{p.label}</div>
      <div class="font-mono text-ink-900">{p.value}</div>
    </div>
  );
}
